import { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

// ═══════════════════════════════════════════════════════════════════════
// SUPABASE — backend connection (fire-and-forget; localStorage is source of truth)
// ═══════════════════════════════════════════════════════════════════════
const SUPABASE_URL = "https://hwjfrnubidvcczaupoft.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh3amZybnViaWR2Y2N6YXVwb2Z0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc3NzY5NzUsImV4cCI6MjA5MzM1Mjk3NX0.9Z0aS6VXDXw1wnd8Qw_vJ5twMXU7MpsnVumOdkBa61U";
const COMPANY_ID = "d15e670f-0cd5-43d4-9fe6-284390fbc7ad"; // BAC Drywall
const sb = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Push a clock event (in/out). Returns the new row id, or null if failed.
const pushClockEvent = async (type, workerId, workerName, siteName, gps) => {
  try {
    const { data, error } = await sb.from("clock_events").insert({
      company_id: COMPANY_ID,
      worker_id: workerId || null,
      worker_name: workerName,
      site_name: siteName,
      event_type: type,
      gps_lat: gps ? parseFloat(gps.lat) : null,
      gps_lng: gps ? parseFloat(gps.lng) : null,
      gps_accuracy: gps ? gps.acc : null,
      clocked_at: new Date().toISOString()
    }).select().single();
    if (error) { console.error("clock_event push failed:", error.message); return null; }
    return data.id;
  } catch (e) { console.error("clock_event exception:", e); return null; }
};

// Push a completed shift + its plot entries. Returns true/false.
const pushShift = async (shiftRecord, entries) => {
  try {
    const isoDate = (() => {
      const [d,m,y] = shiftRecord.date.split("/");
      return y+"-"+m.padStart(2,"0")+"-"+d.padStart(2,"0");
    })();
    const { data: shift, error: sErr } = await sb.from("shifts").insert({
      company_id: COMPANY_ID,
      worker_id: shiftRecord.workerId || null,
      worker_name: shiftRecord.name,
      site_name: shiftRecord.site,
      shift_date: isoDate,
      time_in: shiftRecord.tIn,
      time_out: shiftRecord.tOut,
      total_mins: shiftRecord.totalMins || 0,
      ws_hours: shiftRecord.mgH ? parseFloat(shiftRecord.mgH) : null,
      si_hours: shiftRecord.siH ? parseFloat(shiftRecord.siH) : null,
      status: "submitted",
      submitted_at: new Date().toISOString(),
      manual_entry: shiftRecord.manualEntry || false
    }).select().single();
    if (sErr) { console.error("shift push failed:", sErr.message); return false; }
    if (entries && entries.length > 0) {
      const rows = entries.map(e => ({
        company_id: COMPANY_ID,
        shift_id: shift.id,
        plot_number: e.plot,
        description: e.desc,
        time_spent_mins: e.mins || 0,
        section: e.sec || "mg"
      }));
      const { error: eErr } = await sb.from("plot_entries").insert(rows);
      if (eErr) console.error("plot_entries push failed:", eErr.message);
    }
    return true;
  } catch (e) { console.error("shift exception:", e); return false; }
};

// Push a request (site missing, time edit, manual shift add, etc.)
const pushRequest = async (req) => {
  try {
    const { error } = await sb.from("requests").insert({
      company_id: COMPANY_ID,
      worker_id: req.workerId || null,
      worker_name: req.workerName || req.name || "",
      request_type: req.type,
      note: req.note || "",
      requested_time_in: req.tIn || null,
      requested_time_out: req.tOut || null,
      status: "pending",
      gps_lat: req.gps ? parseFloat(req.gps.lat) : null,
      gps_lng: req.gps ? parseFloat(req.gps.lng) : null
    });
    if (error) { console.error("request push failed:", error.message); return false; }
    return true;
  } catch (e) { console.error("request exception:", e); return false; }
};

// Look up worker by PIN. Returns worker object {id,name,role,pin_temporary} or null.
const loginByPin = async (pin) => {
  try {
    const { data, error } = await sb.from("workers")
      .select("id,name,role,pin_temporary")
      .eq("company_id", COMPANY_ID)
      .eq("pin", pin)
      .eq("active", true)
      .maybeSingle();
    if (error) { console.error("login failed:", error.message); return null; }
    return data;
  } catch (e) { console.error("login exception:", e); return null; }
};

// Update a worker's PIN (and clear the temporary flag).
const updateWorkerPin = async (workerId, newPin) => {
  try {
    const { error } = await sb.from("workers").update({
      pin: newPin,
      pin_temporary: false,
      pin_changed_at: new Date().toISOString()
    }).eq("id", workerId);
    if (error) { console.error("pin update failed:", error.message); return false; }
    return true;
  } catch (e) { console.error("pin update exception:", e); return false; }
};

const LOGO = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAA0JCgsKCA0LCwsPDg0QFCEVFBISFCgdHhghMCoyMS8qLi00O0tANDhHOS0uQllCR05QVFVUMz9dY1xSYktTVFH/2wBDAQ4PDxQRFCcVFSdRNi42UVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVH/wAARCAFAANwDASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD06iiigAooooAKKKKACiikJCjJIA96AFoqhcataQZAfzG9E5rLuNcuJMiFVjHr1Nc9TE06e7NoUJz2R0DyJEu53Cj1JxWdca3bRZEQMp9uB+dc9LLJM26R2c+5zTK4KmYPaCOyGCX2maFxrF3NwrCJfRev51BBf3UBykpx1weRVaiuGWIqSd2zqVGCVrG5b68OBcRY/wBpf8K1ILy3uB+6lVj6d64+lBIOQcH1rpp4+cfi1MJ4OD+HQ7eiuVt9Vu4MDfvX0fmppNcum+4qJ+Ga7o46k1dnK8JUTsjpKK5CTULuX70749AcVYtNYuIMLIfNT36/nSWPpt2B4Oolc6eiqdpqVvdYCvtb+63Bq5XZGcZq8Wc0ouLswoooqyQooooAKKKKACiiigAooooAKKKKAGu6xrudgqjuTiq8l5ZMpDzxFfQkGuZ8Tan9on+yRN+6jPzEfxN/9asMMy9GI+lclXEcrskejQwLqR5m7HaStozfe8r/AICP8Kpypo5+7JIP93Nc2txKv8efrUi3jjqoNcFSo5fZR1xwco/aZqyJZj/VzSn6pVc4zxVVbxD1UipFuIm/jA+tcc030saqnKO5LRSBlboQacATwBmsrDEoqVYGP3uKmWNV6CnYlySKwRj0U0vlP/dq3RT5Secq+S/p+tL5D+1WaKLC52Ot9N8/G25jDemDmtqytrqDCyXIkT0K8/nWH0q5a6hNCwDsXTvnqK7sNVpwepyV4Tnsb1FMjkWRAynIPen17SaaujzdgooopgFFFFABRRRQAUUUUAFZHiDU/sNpsjP7+XhfYdzWlczx21u80pwiDJrz+/vJL67eeT+LoPQelYV6nJHzOvCUPbT12RX60UUV5Tdz6JK2iCiiigYUUqI8jBUUsx7AZrQXQ9QaAy+TjH8JPzH8KpQctkZzqQh8TM7p0rQ0uQlnRjnuKoMrIxVgVYcEEYxU1k+y6Q+vFZyiE0pR0NyiiiuY4AoqaC1luPuLx6npRLazQnDoR71XJK3NbQhzina5DRS7G9KTB9Ki6K5kFFFFAy3Y3htn2tzGeo9K3UdXUMpyDXL1d0+9NuwRzmM/pXfhcTyPllsclejze9E3aKRWDAEHilr2L3PPCiiimAUUUUAFFFZPiDU/sFpsjP7+XhfYdzSbsrsqMXJ8qMXxNqf2if7JEf3UZ+Yj+Jv/AK1YVGc0V5NWbnK59Jh6UaUFFBRRRWR0EtrbS3c6wwjLn1rorPwuow11KW/2U4FM8JWuTLcsP9hf611Nd1ChGUeaR4+Mxc4z5IMrW1jbWq7YYlQewqziiiu1JLRHlOTk7sz9R0m11BcyLtk7OvWuT1DR7vTm3lfMiByHUfz9K7ykKhhgjIrGpQjM6aOKnS03RysAaZV2AsWGRita00vo0/P+yKvw20MHEUaoPYVNXNSwUYu89R1cS5aR0GogQAKMAUpUMMEAiloru5VaxyFObT4n5T5D7dKz7i2kt8b8EHoRW5Ve9i822YDqORXBicDTnFyirMtSaMQgHqKjaP0qWivnVJxZvGpKOxX6UVOVB6io2jI6c1vGonudUKye5oaRctvMDHK4yvtWvXOWL+XeRn3xXRCvewNRyhZ9DkxMUpXXUWiiiu45gooooAKjkhik5dFJ9SKkrF8S6h9ksvJjOJZuPoO5qZNJXZdOLlJKI281bSbXKqqzOOyKP51g3etTT5EMMcC+ygn86zKK8yddy2PepYSMNZasCSxJJyTQAScAc0Vc0ZI59Yt4XYcnOPXHNZRi5uyOmpONOPMzs9ItfsmnxRY5A5+tUr7xHb6dfta3kEqcZV1GQw9a2gMCsPxXpX9oaaZYlzPB8y+47ivboxirRex8pXnKV5LcuWuuaXdYEV5Hk/wsdp/WtBWDDKkEe1eQeVJ/zyf/AL5NbHhe/kstZijd2Ecn7sqTwCenFdk8KkrxZwwxbvaSPSKKOtFcR3hRRRQAVWuL60tR++nRPYnn8qpeJZmh0ltjFWZguQcGuK5Y55JrKpU5NDmrV/ZuyR1tx4mtlO23ieZjwOwNbMDSPCplUK5HzAdBXKeG9Oae7+0SoQkXTI6tXXgYopuUtWOhOc/ekYdzF5Vw6ds5H0qKtHVYx8knfpWdXzWNpeyrNdzti7oKKKK4yhMAMG7g5roY2DICO9c/WxYPvtl9RxXrZZUtUce5M22i1RRRX0BkFFFFADZHWKNpHOFUZJrz7U71r++ec/dPCj0FdB4r1DZGLGM/M/zSfTsK5WuHFVPsI9fL6H/LxhSEgDJOBUcsyRDnk+lUpZnlPJ49KWGwVSu+yNsZmNLCrV3fYnmuv4Y/zo024NtqdtcE/ckBP071Vor6KhhKdGNkj5DFY+riZXk9Ox7Cpyopao6NcfatJtpu7RjP171erzZKzaPQg+aKY3avpXE+NrA295FqEQwJPlYjsw6H/PpXcVR1ixXUdMntj95lyp9GHSrpT5JJkVaanFoXSLwX+mQXI6uvP17/AK1drhfh5qsjz3ml3Hyuh8xFPbsw/Ou6pVFaTsVTvyq4UUUVBZieJY3uI7W1j+9LL+QA61pW1rFbwJGigBRjpUjQo86SsMsgIX2z/wDqqWp5dbmfIubmYgAFLRRVGhz3iW78m6tIweAS7fTp/jTevSsrxFN52sSjsgCCrmnTedaLk/MvymvGzOnzRU10OajXvXcC1RRRXhHoBWhpT/fT8az6sWL7LpfQ8V04WfJVixPY2qKKK+tMQqC8uUtLWSeQ/Kgz9anrjfF2roZxZI2RHy4Hdv8A61J3toXBJv3nZGRdXD3FxJcTN8znJ9qoTXX8Mf50wrc3J+WKRh2CqTViLRdUl+5YzfiuP51ph8BFPnrP5DxWau3ssMtO5QJJOTRVi9sbiwlEVygSQjO3cCQPfFV69mKil7ux87NybvLcKKKKog77wRcebpDQk8xOR+B5FdJXC+BbjZfz25PEiBh9R/8Arruq8jER5ajPYwsr00FFFFYHSeceJFPhrxtbavEp8i4+Z1Xv2cf1rZX4iaKfvR3S/wDbMf41o+LPD/8Ab+npEkoinibfGxGQeOQa8l1PTL3Srk297A0T9j2b3B710QUZqz3EenL4/wBBPWSdfrEa0NK8UaTq10LWzuGaYqWCshXgfWvFq7PwL4f1M6pb6oU8i2jycuMGQEYwB6e9OVKMVcD0+iiiuYYU12CIWPQDJp1UNbn8jSbhwcErtH48UCbsrnDzyGa4klPV2LfnVzSJdk7RE8OOPqKz6dE5jkVx1U5rlrQ9pBxZ4MajjU5zp6KajB0Vx0YZFOr5WUeV2Z9JF3VwpVba4YdjmkopLQo6BGDICKdVawffbL7cVZr6+hPnpqRi9wqutlaqxZbeIMTknYM1Yora9iWrjQqgYAArN17V4tIsi5w0z8Rp6n1+lWtRvoNOs3uZ2wq9B3Y9gK8y1PUJtTvHuZzyeFXso9BW9Gk6j8jCvVVKOm5BPPJczvNM5eRzliajoor1kklZHjttu7CiiimItabfSadfR3UQDMnY9CD2r0HRvEFhq+Y4ZQtwo+aFj8w/xryq9ufJTap/eH9KzIppYZlmikZJFOQynBBrkxFOM/U9HBqS16Hv9FcB4a8ehtlrrJCnotyBwf8AeH9a72ORJY1eNgyMMhlOQa86UXHc9AdVPVLC11Cxkhu4FlTaSAw6H1HpVqSRI0LyOqKvJLHAFclrnjvTLRHgswbyXBGU4Qfj3/CiKbegGN8OtHsb1rq7uYBLJBIFjDcqOOuPWvRwAOleU+C/E9toZnhu4nMc7hvMTnb26V6ZYalZalD5tlcxzJ/snkfUdquqpX1At0UVha74mtNJUxIRNddo1P3fqe1Yt2KjBzdomtd3dvY27T3Mqxxr3NcTqXiU6vM1tBHstl+YFvvOf6Cud1PVLvVLjzbqUt/dUcKv0FM05sXaj1BFZOd9EehLActCUp72Neiiig+JNvSZvMtdhPKHH4VeBwc1haXN5V2AT8r/AC/4Vu189j6fJVuup7uBqc9O3YupbR3Ue+IhH7r2zVaWGSE4dSPftS205glDDp3FbQKSx54ZSK2oUKeKhZaSR1t2KGlP99PxrSqtHaJFN5kZwOhFWa9bCU50qfJPoQ3cKZLIkMTSyMFRAWJPYU+sHxld/ZtDeMHDTsEH06muyK5mkRKXKmzkPEGsyateZGVt4+I0/qfesqiivahBQVkeJUm5yuwoooqzMKhuZ1gj3Hlj0FPllWGMux4FYs0rTSF2/AelROXKjooUXUfkNd2dizHJNJRRXM3c9ZJJWQVtaF4n1LQwUt3EkB/5ZSZKg+o9Kxa6jw74LvNXRLqdxbWjchurOPYf1NRLlt7xRmXuq6x4guVillluGY/LDGPl/BRXU6B8PmbbPrD7R1Fuh5/E/wBBXZaRoen6ND5dnAFJ+9IeWb6mtKuaVXpEZx+ueArC9XzNPIs5gMBeqN9R2/CuCvNP1jw5eBpFltnB+WaM/K30I/lXttRzwQ3MLQzxJJG3BVhkGlGq1owPMIvGWsXOmm3eVA+cGZVw5H8vxrHJLEkkknkk12+q+BYMPLpTeUSc+S5yv4HtXGXNvLaXD286bJEOGX0rlrO8rrY9zAOnyabkVS2rbbmM/wC1UVKpwwPoaxO+avFo6CigHIBorU/NKseWbQoJUgjqOa6SCQTQJIP4hXNVr6PNmN4Sfu8iuDH0uenfsdOBqclS3c0qu6dcbH8pvunp7VSpVYqwYdQc14lGq6U1JHutXOhopkTh41YdCM0+vrYyUoqSMQrg/HN35upxWoPywpk/U/8A1sV3bMFUsTgAZNeT6ldG81G4uT/y0ckfTt+ldmFjzTv2OXFT5YW7laiiivVPICkZgilmOAKjmnjgXLnnsO5rLubp5+Pup6VMpKJtSoyqPQS6uDcSZ6KOgqGiiuZu7uetCCgrIKKKVFLsFHU0iyS3i8x8n7o61634HuPO8OxJnmFmj/XP9a8wRAiBRXdfDq4+W8tiehVx/I/yqK0fcN+S0Tt6KKK4DIKKKKACvINZm+0axeS54aVsfTOK9aupBDayynoiFvyFeMsxZix6k5rKpsenl0bzbEooorE9o3bdt1vGf9kVJVbT23Wi+2RVmtFsfnWPhyYia8wqxYzeTdIx6E4P0NV6KUoqSaZyRlytNHU0VXspfOtUbvjB+oqxXytWDhNxZ9NTmpwUkaumSboSh6qau1jafJ5dyB2bitmvocuq89K3YUlqZPie7+x6FcMDh3Hlr9TxXmddd48u8y21mp+6DI38h/WuNmmjhXLn6Dua+jwkbQ5jysXJynyokqjc36plYfmb17UkUWoavL5VlbSyj0QZH4muisvANwlu11q10ttDGpdkj+ZsD36CtKleMdCqOF6zONdmdizEkn1pKVypdigITPyg9cdqbkeorO99TuSS0QtFJkeooyPUUDFq7bxeWuT941DbRhjvbGB0q5VRRtTj1YVf0fVrjR7s3FuEYsu1lfoRVCiqaurM3PRNO8cWM+EvI3tn/vfeX8+tdJbXdvdxiS3mSVPVGzXi9S2889tIJLeV4nH8SHBrmlh09jN010PaHdUUs7BVHUk4ArC1HxdpNllVmNxIP4Yef16V51eajfX/APx9XUsoHZjx+VVKmOH/AJmJU+502qeMr2/jkghiS3hcFT/ExB965ymr1p1ceKgoSSR7GBilB2CiinKrOcIpY+gGa5TvuaWltmF19Gq7VLTLe4RpA0EqgjPKGruMHBrSOx8NnULYptdQooopnjGno0uHeE9CNwrWrn9Pfy72M9icfnXQV4OYw5ail3Pcy+d6fL2FUlWDDqOa3YpA8asOhGawavWl0scIRj0PFRgsR7GTvszuauctqOi6vrmsTzxRCCEttWWbj5RwML1NaOm+A9Mt2Et8z3sv+3wv5CtDxT4gHh6yjm+zmZ5WKKu7ABxnmvOdU8Za1qO5Tc/Z4j/BB8vH1619gpTmrLRHLGnGL5up63bR20KGG2SJFTgpGANv4CuZ+IupfY9BFojYku22cf3Ryf6Cr/gzT20/w9AJAfOm/fSE9cnp+mK8/wDHmpf2h4jlRGzFbDyl+v8AF+v8qzjG87Gpzsf+sT/eFe6JptgUX/Qrfp/zyX/CvC4/9an+8P5179H/AKtfoK1ru1hIrf2ZYf8APlb/APfpf8KP7MsP+fG3/wC/S/4VbrHufE+j2txJbzXW2SNtrDaeD+Vc65nsO1y9/Z1l0FnAB/1zFV7nQdKuVIlsIT7hdp/MUlhr2l6jN5Nrdo8nZTkE/nWnReSHdo868TeEzpsTXlkWe3X76NyU9/cVyte13EazW8kTjKupUj2xXirgK7KOgJFdlCo5aM2pyvoy5o1i2parb2gHDt8x9FHWvW0tYFQKIUAAwPlFch8PtO2xTai68v8Au4/oOp/P+VdtWWIneVl0JqS1sRfZof8Ankn/AHyK4r4gaWqeRqEKAD/VyYGPof6V3VU9WsV1HTJ7Rv8AlopAPoex/OsYTcZXIUrM8dXrUsUbzSLHGhd2OAo6k0x0aKZo3GHUlSPQiu98FaKsNsNQnX97KPkz/Cv/ANeqxa5pI9ahXVKk5Mj0XwZGEWbUjvfr5QPyj6nvXVW9jbWqBYII4wOyqBVmkJA61gopHnVa9Sq7yYBQO1VrmwtbpcTQo3vjn86kS6t3fYk0bN6BgTU1Uc8op/Ejj9X0B7RTPbEyQjqp6r/jWJXpLlQpLkBe+elcFqq2q38n2Rw0R546A+gqJI8vFUIw96JUVtrBh2Oa6ZG3IrDuM1zFdBpz+ZZRnuBj8q8rMoXpqXYeXztNruWakjiaRcj1xUdadimLYH1JNeNSpuo7I9o5L4pt/omnJ6yOf0FcX4d046prtraYyjPuf/dHJrrfim/z6ans5/lTvhhpvy3WpuvX9zH/ADP9K+6g+WncwOw1u/TSdEubvgeVH8g/2ugH514e7M7l3OWY5J9TXoPxO1LC22lo3X99IP0X+tee06MdLgx0f+tT/eH869+j/wBWv0FeAx/61P8AeH869+j/ANWv0FKv0BDq8+1fwnq13q11cxLD5cshZcyYODXoNFYwm4O6Li7HCaD4Qv7XVILq6eNEhbdhGyWNd32ozSMNylckZGMiic3N3YSk2Y3ibWodK06QbwbmRSsad/r9BXlsET3E8cMY3PIwUfU12XiTwjN+8vrOeW4b7zxync34Hv8ASqXgTTvtOqNduv7u3HH+8f8A61dNLlhByNIWSud7plmlhp8NrH92NQv19TVqiszxFf8A9m6LcXAOH27U/wB48CuT4mZbsuWl3DeQ+bA+9NxXPuDg1PXDfD3UDuuNPduv71M/kf6V3NVOHK7DkrM878WaOU8TQNGuI75x+DZwf8a9AhjWGJY0GFUYA9qrajYJe/ZmON8Eyyqfp1/SrvaiUrpIbk3FIK898Z65NNfPp8EjJBFw+043t3z7CvQq8h1pWTWr1X+95zfzrGo7I68DTjOp7xUjcxyK6khgcgjg16Bp/iTybIrchpJV+4R/EPc1wdnF5tyo7Dk1tVlBsxzusqTiobl7UNVur9sSPtj7Rr0/+vVGiirvc+WnUlN3kwrX0Z8xSR+hzWRV7SH23e3+8uK5sVDnpNGmGly1UzbrcgTZCi+grGhXfMi+pFbyjAFedllPmlJn0MmecfFAl9T0+NRk+W2B9WxXbaBp66VoltaYwY0y/wDvHk/rWFrFh/aXxA05WXMVtb+c/wCDHH64rr6+jlL3UjM8O8Q6g2qa5dXZztZyEB7KOB/Ks6tzxlpv9meI7mNVxFKfNT6N/wDXzWHXXC3KrCHR/wCtT/eH869+j/1a/QV4DH/rU/3h/Ovfo/8AVr9BWNfoCHV5L4gmmXX78CWQATNgBjXrVRmCJiSY0JPfaKypz5HcuLszyTS7nUxfxfYpJ2l3DAUkg/X2r15c7RnrTUijT7qKv0GKfTqVFPZDlK4VUsNPg09ZVgXaJJGkP1NW6KyuQFU9R0201OFYryLzEU7gMkc/hVtiFUsTgDkmsr/hJdF/6CMP500n0GrhZeHdLsblbm2tvLlXOGDt3/Gtasn/AISXRf8AoIw/nV2zvrW+iMlrMsqA4yvrTlzfaG03qWaKKZLKkK7pGCrkDJ6c1JI+uQ8WeGZb6c31iA0pGJI843e4966+ik1fQ0pVJUpc0TzXS9B1Jd26zkRicfMMACux0vRIba1ZLhFleT72Rx9BWziikopGVde3q+1mcvqPhplzJZNkf882P8jWPa2Mk+oLaSBomOc5HI4rvJ5o7eFpZWCooySa53TLptT197gjCRoQg9BmhpHn1sPTUlbqYd7ZT2M3lzLj0YdGHtUdq/l3Mb+jCu9ura3voWhlCuBwfVT/AErjtV0mfTpN3Lwk/K4HT61E4aGNbDOm+aOx0OnruugfQE1s1k6GfMgE3qorWrkwFL2cH6nrKXNFMrx2iJfTXfWSRFT6AZ/qasUUV6AziPibpvnabBqKL80DbH/3W/8Ar/zrzSvd9Usk1HTbizf7syFfoe3614XNE8EzwyDDxsVYe44rqoS0sJjUIDqT0BBr1uHxxobphZpeP+mRryOrloMRk+prWVNT3Kgrux6p/wAJpov/AD1l/wC/Zo/4TTRP+esv/fs15lRS+rRNvZo9N/4TTRf+esv/AH7NVrnx3psa/uIJ5W9wFFcVDo2ozwJNFbFo3GVO4DI9hmqc0MsErRTRtHIvBVhgihYeAezR32neNrN4ne/YxOW+WONCQq/Xuauf8Jron/PWX/v2a4T+wNVwP9EbOM43Ln8s1TS0uJDMFiYmEbpF7qB14odCDD2aO31zxfYT6RPBYySGeRdgyhGAev6VwVSwW81yzLChcqpc47AdTQ9vLHFFK6ERyglG7HHBq4wjTRcIW0RGo710fhLXItJmmS6ZhBIAeBnDCsOS3mhiikkjKpKNyE/xCklglhWNpEKiRd657j1rzK1ZzndbHrww8PZ8kup6P/wmWjf89ZP+/ZqK68V6FdW0tvJJIUkUqf3Z715/NazwRRSyxlUmG5Cf4hTUhleGSZUJSPG8+melZe0ZisBS3udFpHi650/9xcZu7dThWJw4Hb611tj4n0q9XKzmNh1WRSMf0ry2tq2tZLRDHMmyXOWB7URmzmzKnSw9J1Op6GdX04DJu4v++qp3XiWyiBEO6ZvYYH5muOorTmPmJY6T2Rd1HVLnUH/enbGOiL0FbHhGLi4l9wv+fzrmq6/w0nk6QZT0Ys3+fyojqzOhKVSqnIwZtVns9dup4WypkIZD0YDiuqsL611a1O3ByMPG3UV5/I2+RnPViTXV+EbLy4Xu3GGk4X/dFdMorlKoVpyrOK2Zu2dnHZQ+VFnZkkA9varFFFYJJbHrJJKyCiiimMK8l+IOnfYvETTquI7pfMH+90P+P4161XKfETTvtnh/7Sq5ktG3/wDATwf6H8K0py5ZAeUVethiBao1rWNtJczQW0Qy8hCj8a74l0tzQXSyPDsmpuCMzLGn05yfzxWZXpPiSwjtfBb20Q+WBUx+BGTXm1TTnz3NoyuaWrcWul/9eg/9CapNSZ5NF02S4JNwd4Bb7xjBG3P45xUMWs3UcEUOy3dYl2oXhViB9TVS6uZ7uYzXEhkc8ZPp6D0FUosdjo9Q021vdfkX+0Ck5VW8pYju4QcKc4JrJuNVk/t19RhTYd33G7jGCG+o61UnvJ57v7W74myDuXjBHA/lTbq4kurh55du9zltq4BPrihR7gka+rvb2VqtvYqyLeqJ5C3UKfuoPYf4UzT4m1HSJLFBmaGVZY/91vlb+hrNmuJbnyjKQfLjEa4GPlHSpLK8nsZ/Pt32vtK5xng1wYirb3Ed+Hw7cebr0N25Uausljb4P2OZEix/zzPyn9RmqMsa6r4g8iMhYFIjB7LGvf8AT9aoWd5PZTNLbvtdlKE4zwetNhuJIElWMgeamxjjnHpXFc640pReh0N1b3F9bXsbmHCHzbZUlViAowVAH+yPzrKsv+QHqf8A2y/9CqlbTyWtwk8J2yIcg1Pa6jPaecI1iKzYLq8YYcHI4NF0CpSimkV4E82dEHc11Or4GqTj0I/kKy7a5ku/nkigQJ93y4wvP4Vqtqc7tueOBmPUmIE1UUfN53ieeSovoUqKUnJJ9aSmfNhXZL/onhdj0Ig/Uj/69cfGnmSog/iIH511/iNxDoDoP4iqfr/9atILU7MJpzS7I46ytmu7uK3Tq5wT6Dua9Gt4VggSJBhVAAFc54RscK9645b5U+neuorao+h0YGlZOb6hRRRWZ6AUUUUAFR3ECXNvJBIMpIpVh7GpKKAPBr+zex1Gezk+9FIU/Xiu48AaZ513JqDr8kI2R/7x6/kP51W+IWlMmv2t3CmRdgIQB1cf4jFd1oenrpmlQWo+8q5c+rHrXVKp+79SouyYzxHH5vh++T/pix/LmvI69nv082wuI/70bD9K8Yxjiqwz3RpSCiiius1ClAzSU8DFc+Iq+zj5nRh6XtJeQUUUV4rd3dnspWVkFFT2dpcXs4gtomkc9h2+vpXdaF4QgtNs99ieYchf4V/xqoxbMa2IhRXvHCy2lxDbxTyxMkcudhbjdioa6rx/KDqVtbr0jizj6n/61c5ZxebcqD0HJokrOxNOvzUnVloalrH5Vuid8ZNS0UVaPz7E1nWqym+oUUVr6JpDXziaUEQA/wDff/1qaVzKEHN2Q/QNKe4mS6kBWJDlf9o/4VpeJ43uEs7OP70sv5YHX9a3I41jQKoAAGAB2qJ7YPfJcNz5aFVHoT1Nax0PXhh1CnyrqOtIEtrdIUGFQACpqKKGdMYqKsgooooKCiiigAooooAr3VnBdtC0yBjDIJE9mH/66sUUUAIwypB6HivFblPLupo/7rsP1r2uvHtdj8nXL5PSZv5104Z+80a09yjRRSqM12Skoq7OiMXJ2QqjFLRVmysbm/nENrEZHPp0H1NeJVqOrK57VOEaUCtXQaH4Wu9SKyz5gt/Uj5m+g/rXSaF4St7LbNd4nnHPT5V+grfvLu1062aa5kWONe57+wojT6s4K+O+zSItO0y00y3EdvEEHc9z9TRaapbXl1NBbP5nk4Duv3cntnvXCa/4qudS3QW26C16dfmf6+3tXSeCLT7PogmYYMzF/wAOg/lVqSvZHJUoTUHVqbs5LxfN5/iS654TCfkKh0yLbE0h6twPpVW/lN3qdxKOTJKxH4mtWNBHGqDooxWW8hZlW9hg1TW7HUUVp6NpL38u9wVgU8n+97CrSufIRi5uyHaLpDX8gkkBFup/769q7OKNYowiAKoGAB2pIYkhjVEUKqjAA7VJWiVj2qFBUl5hRRRTOkKKKKACiiigAooooAKKKKACiiigArynxfH5fia8H94hvzAr1avNPHsezxDu/vxKf5it6DtM0p7nNgZp4FWdO0661CYQ2sRdu57L9TXfaF4UtrDbNc4nuBzkj5V+gqMTUdR8sdj1Kc4YePNPfsc3ofhS61DbNdboLc9v4m/DtXfWGnWunQCG2iVFHp1P1qw7Rwxl3YIijJJOAK4zXPF0k0hs9HDMTwZgMk/7o/rWCSgcs6tXFSstjb13xJaaQpjBE10RxGp6fU9q871LUrvVLnzbqQuf4VHRfYCtnTvCOoXzedeN5Cscnd8zmuv0zw7p2mgNFCGk/wCej8t/9apalI2hKhhv70jhtM8L6lqGGMf2eI/xSdfwFd/MqaZoMipwsEBA/AVoAAdBWJ4xn8nw3c84L4QfiapRUUc9XESxElF7Hnemx77jeeijP41q1WsIvLtgSOW5ra0jS5NQm5ysKn5m9fYVnFHkZtVdfEezj00F0fSpNQm3NlYFPzN6+wrtYIUgiWONQqqMACkt4I7eJY41CqowAKlrZKxWHoKkvMKKKKZ0hRRRQAUUUUAFFFFABRRRQAUUUUAFFFFABXPa54bGs6rBcSS7IY49rKv3mOc/hXQ0U07bDi3F3RVsrC2sIBDbRLGg7Ci8vY7RBlWkkb7scYyzfh/WrVN2KGLbRk9TSG3d3ZzdzpOpa7IDqU32W1ByLaI5J/3j61r6fpFjpybbaBUPdupP41fopWKdWTXKtEGKKKKZmFcr47cvZ2dqD/rZsn6Af/Xrqq57WtPl1PWoIxlYoY8s/pk9vfApPYcZcj5jE0nS5NQmCjKwr95v6Cu2treO2hWKJQqqMAUlrbRWsKxRKFVelT0JWOalR5W5S3YUUUUzoCiiigAooooAKKKKACiiigD/2Q==";


// ═══════════════════════════════════════════════════════════════════════
// CONFIG — swap this block to white-label for a different company/trade
// ═══════════════════════════════════════════════════════════════════════

const COMPANY_NAME = "BAC Drywall";
const BUILD = "v17.1 - 3 May (Login)";

// BRAND COLOURS — change these to match the company
// BL (primary blue), GR (green/confirm), RD (red/alert) are used throughout
// These are defined later in the component as inline constants.
// To rebrand: search BL/GR/RD below and replace hex values.
// Current: BL="#2F80ED", GR="#27AE60", RD="#EB5757"

// SITES — name, postcode, contractor name, short location label
const SD=[
  {name:"Bellway Bexhill",pc:"TN39 4TW",ctr:"Bellway",loc:"Bexhill"},
  {name:"Bellway Burgess Hill",pc:"RH15 9QU",ctr:"Bellway",loc:"Burgess Hill"},
  {name:"Bellway Crawley",pc:"RH10 3FF",ctr:"Bellway",loc:"Crawley"},
  {name:"Bellway Durrington",pc:"BN13 2QG",ctr:"Bellway",loc:"Durrington"},
  {name:"Bellway Midhurst",pc:"GU29 9PY",ctr:"Bellway",loc:"Midhurst"},
  {name:"Bellway Raynes Park",pc:"SW20 9AB",ctr:"Bellway",loc:"Raynes Park"},
  {name:"Brookworth Maresfield",pc:"TN22 2HA",ctr:"Brookworth",loc:"Maresfield"},
  {name:"Brookworth Rottingdean",pc:"BN2 7HB",ctr:"Brookworth",loc:"Rottingdean"},
  {name:"Crest Hailsham",pc:"BN27 3PH",ctr:"Crest",loc:"Hailsham"},
  {name:"Croudace Ringmer",pc:"BN8 5LB",ctr:"Croudace",loc:"Ringmer"},
  {name:"Customer Care",pc:"",ctr:"BAC Drywall",loc:"Customer Care"},
  {name:"Hills Tunbridge Wells",pc:"TN2 3UY",ctr:"Hills",loc:"Tunbridge Wells"},
  {name:"JVB Chalfont St Giles",pc:"HP8 4QF",ctr:"JVB",loc:"Chalfont St Giles"},
  {name:"JVB Esher",pc:"KT10 8AJ",ctr:"JVB",loc:"Esher"},
  {name:"JVB Monks Gate",pc:"RH13 6JN",ctr:"JVB",loc:"Monks Gate"},
  {name:"JVB Radlett",pc:"WD7 8NW",ctr:"JVB",loc:"Radlett"},
  {name:"JVP Felbridge",pc:"RH19 2PP",ctr:"JVP",loc:"Felbridge"},
  {name:"Office",pc:"",ctr:"BAC Drywall",loc:"Office"},
  {name:"SDP Copyhold Lane",pc:"RH17 5EB",ctr:"SDP",loc:"Copyhold Lane"},
  {name:"SDP Naldretts Farm",pc:"RH16 1XR",ctr:"SDP",loc:"Naldretts Farm"},
  {name:"SDP Newick",pc:"BN8 4LE",ctr:"SDP",loc:"Newick"},
  {name:"SDP Redhill Farm",pc:"RH1 4AH",ctr:"SDP",loc:"Redhill Farm"},
  {name:"SDP Wineham",pc:"BN5 9AY",ctr:"SDP",loc:"Wineham"},
  {name:"Taylor Wimpey Friars Oak",pc:"BN6 8EH",ctr:"Taylor Wimpey",loc:"Friars Oak"},
  {name:"Taylor Wimpey Hellingly",pc:"BN27 4EP",ctr:"Taylor Wimpey",loc:"Hellingly"},
  {name:"Taylor Wimpey Horley",pc:"RH6 8EZ",ctr:"Taylor Wimpey",loc:"Horley"},
  {name:"Vistry Kirdford",pc:"RH14 0NQ",ctr:"Vistry",loc:"Kirdford"},
  {name:"Vistry Summer Hill",pc:"RH13 6QA",ctr:"Vistry",loc:"Summer Hill"},
];
const SITES=SD.map(s=>s.name);
const PC=Object.fromEntries(SD.map(s=>[s.name,s.pc]));
const CT=Object.fromEntries(SD.map(s=>[s.name,s.ctr]));
const LC=Object.fromEntries(SD.map(s=>[s.name,s.loc]));
const DOTS=["#2196F3","#4CAF50","#FF5722","#9C27B0","#F44336","#FF9800","#795548","#009688","#3F51B5","#E91E63","#00BCD4","#8BC34A","#FFC107","#607D8B","#673AB7","#F06292","#4DB6AC","#90A4AE","#A5D6A7","#EF9A9A","#80DEEA","#CE93D8","#FFCC02","#66BB6A","#42A5F5","#EC407A","#26A69A","#FFA726"];

// DAYWORK SHEET SECTIONS — two work-type tabs and sheet labels
// Drylining: "Work Sheet" + "Site Instruction Works"
// To adapt: change tab/sheetLabel text for the new trade
const SHEET_SECTION_A={key:"mg",tab:"📝 Work Sheet",sheetLabel:"Work Sheet - Please Include Plot Numbers",hoursLabel:"Hours"};
const SHEET_SECTION_B={key:"si",tab:"📋 Site Instruction",sheetLabel:"Site Instruction Works - Please Include Plot Numbers",hoursLabel:"Hours"};

// TRADE VOICE CORRECTIONS — see fixWords() function below
// All drylining mishear corrections live there. To adapt for another trade,
// replace the fixes array with trade-specific patterns.
// ═══════════════════════════════════════════════════════════════════════
// END CONFIG
// ═══════════════════════════════════════════════════════════════════════

// Parse time from any format - walks through string finding hours and minutes
// Handles: "2", "2h", "2hr", "2hrs", "2HRS", "30m", "30min", "30mins", 
//          "2h30m", "1hr 30min", "1 hour 30 minutes", "1.5", "1.5h", "90"
const pMin=t=>{
  if(!t)return 0;
  let s=String(t).toLowerCase().trim();
  // Colon format: "2:30" = 2h 30min
  const colon=s.match(/^(\d+):(\d{2})$/);
  if(colon)return parseInt(colon[1])*60+parseInt(colon[2]);
  // Decimal hours: "1.5" or "1.5h" or "1,5" or "2.5hrs"
  const dec=s.match(/^(\d+)[.,](\d+)\s*(?:h|hr|hrs|hour|hours)?$/);
  if(dec){const whole=parseInt(dec[1]);const frac=parseInt(dec[2])/(10**dec[2].length);return Math.round((whole+frac)*60);}
  let mins=0,found=false;
  // Find hours component
  const hMatch=s.match(/(\d+)\s*(?:hours?|hrs?|h)(?![a-z])/);
  if(hMatch){mins+=parseInt(hMatch[1])*60;found=true;s=s.replace(hMatch[0],"");}
  // Find minutes component - also catches plain number after hours e.g. "2hrs 30"
  const mMatch=s.match(/(\d+)\s*(?:minutes?|mins?|m)(?![a-z])/);
  if(mMatch){mins+=parseInt(mMatch[1]);found=true;}
  else if(found){
    // Check for trailing number with no unit e.g. "2hrs 30" -> 30 mins
    const trailing=s.match(/^\s*(\d+)\s*$/);
    if(trailing){const n=parseInt(trailing[1]);if(n<60)mins+=n;}
  }
  if(found)return mins;
  // Half/quarter hour
  if(/half\s*(?:an?\s*)?hour/.test(s))return 30;
  if(/quarter\s*(?:of\s*an?\s*)?hour/.test(s))return 15;
  // Plain number - if <=12 assume hours, else minutes
  const plain=s.match(/^(\d+)$/);
  if(plain){const n=parseInt(plain[1]);return n<=12?n*60:n;}
  return 0;
};
const fMin=m=>{if(!m)return"";const h=Math.floor(m/60),mm=m%60;return h&&mm?h+"hr "+mm+"min":h?h+"hr":mm+"min";};
const fSec=s=>String(Math.floor(s/3600)).padStart(2,"0")+":"+String(Math.floor((s%3600)/60)).padStart(2,"0")+":"+String(s%60).padStart(2,"0");
const gPlot=es=>{const map={};es.forEach(e=>{const k=e.plot.padStart(8,"0");if(!map[k])map[k]={plot:e.plot,items:[],mins:0};map[k].items.push(e);map[k].mins+=e.mins;});return Object.keys(map).sort().map(k=>map[k]);};
const saveShift=r=>{try{const ex=JSON.parse(localStorage.getItem("bac_shifts")||"[]");ex.push(r);localStorage.setItem("bac_shifts",JSON.stringify(ex));return true;}catch(e){return false;}};
const getShifts=()=>{try{return JSON.parse(localStorage.getItem("bac_shifts")||"[]").sort((a,b)=>b.id-a.id);}catch(e){return[];}};

const BG="#EEF2F7",W="#fff",BL="#2F80ED",GR="#27AE60",RD="#EB5757",BD="#D1D5DB",MT="#6B7280",DK="#111827",LBL="#EBF3FD",SBL="#BDD6F1";

export default function App(){
  const [sc,setSc]=useState("home");
  const [showS,setShowS]=useState(false);
  const [srch,setSrch]=useState("");
  const [recent,setRecent]=useState(["SDP Redhill Farm","Taylor Wimpey Friars Oak","Bellway Burgess Hill"]);
  const [site,setSite]=useState("");
  const [ctr,setCtr]=useState("");
  const [tIn,setTIn]=useState("");
  const [tOut,setTOut]=useState("");
  const [name,setName]=useState(()=>{try{return localStorage.getItem("bac_name")||"";}catch(e){return"";}});
  const [workerId,setWorkerId]=useState(()=>{try{return localStorage.getItem("bac_worker_id")||"";}catch(e){return"";}});
  const [workerRole,setWorkerRole]=useState(()=>{try{return localStorage.getItem("bac_worker_role")||"";}catch(e){return"";}});
  const [pinInput,setPinInput]=useState("");
  const [pinError,setPinError]=useState("");
  const [pinChecking,setPinChecking]=useState(false);
  const [showPinPrompt,setShowPinPrompt]=useState(false);
  const [showPinChange,setShowPinChange]=useState(false);
  const [newPin1,setNewPin1]=useState("");
  const [newPin2,setNewPin2]=useState("");
  const [pinChangeError,setPinChangeError]=useState("");
  const [pinStep,setPinStep]=useState(1);  // 1 = entering new, 2 = confirming
  const [showSettings,setShowSettings]=useState(false);
  const [entries,setEntries]=useState([]);
  const [plot,setPlot]=useState("");
  const [desc,setDesc]=useState("");
  const [tSpent,setTSpent]=useState("");
  const [sec,setSec]=useState("mg");
  const [mats,setMats]=useState("");
  const [elapsed,setElapsed]=useState(0);
  const [nowStr,setNowStr]=useState("");
  const [gpsIn,setGpsIn]=useState(null);
  const [gpsOut,setGpsOut]=useState(null);
  const [gpsErr,setGpsErr]=useState("");
  const [scale,setScale]=useState(1);
  const [submitted,setSubmitted]=useState(false);
  const [listen,setListen]=useState(false);
  const [lisF,setLisF]=useState(null);
  const [quickEntry,setQuickEntry]=useState("");
  const [showQuick,setShowQuick]=useState(false);
  const [voiceStatus,setVoiceStatus]=useState("");
  const [activeField,setActiveField]=useState(null);
  const [fromSummary,setFromSummary]=useState(false);
  const [pastShifts,setPastShifts]=useState([]);
  const [showEdit,setShowEdit]=useState(false);
  const [editShift,setEditShift]=useState(null);
  const [editIn,setEditIn]=useState("");
  const [editOut,setEditOut]=useState("");
  const [editNote,setEditNote]=useState("");
  const [editDone,setEditDone]=useState(false);
  const [showAdd,setShowAdd]=useState(false);
  const [addSite,setAddSite]=useState("");
  const [addDate,setAddDate]=useState("");
  const [addIn,setAddIn]=useState("");
  const [addOut,setAddOut]=useState("");
  const [addNote,setAddNote]=useState("");
  const [addDone,setAddDone]=useState(false);
  const [showAddSite,setShowAddSite]=useState(false);
  const [addSrch,setAddSrch]=useState("");
  const [offlineQueue,setOfflineQueue]=useState(()=>{try{return JSON.parse(localStorage.getItem("bac_offline")||"[]");}catch(e){return[];}});
  const [isOnline,setIsOnline]=useState(navigator.onLine);
  const [weekSummary,setWeekSummary]=useState(null);
  const [pdfUrl,setPdfUrl]=useState(null);
  const [showPreview,setShowPreview]=useState(false);
  const [showSiteRequest,setShowSiteRequest]=useState(false);
  const [siteRequestNote,setSiteRequestNote]=useState("");
  const [siteRequestSent,setSiteRequestSent]=useState(false);

  const wrapRef=useRef(null);
  const tmRef=useRef(null);
  const SHEET_W=680;
  const SHEET_H=900; // Full sheet height including signature row at bottom

  const today=new Date().toLocaleDateString("en-GB");
  const todayISO=new Date().toISOString().split("T")[0];
  const hr=new Date().getHours();
  const greet=hr<12?"Good morning":hr<17?"Good afternoon":"Good evening";

  const card={background:W,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 1px 3px rgba(0,0,0,.08)"};
  const inp={width:"100%",padding:"11px 13px",border:"1.5px solid "+BD,borderRadius:10,fontSize:15,boxSizing:"border-box",background:"#FAFAFA",fontFamily:"inherit",outline:"none",color:DK};
  const lbl={fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:5,display:"block"};
  const C={border:"1px solid #444",padding:"3px 6px",fontFamily:"Arial,sans-serif",fontSize:11,color:"#111",verticalAlign:"middle"};
  const CB={...C,background:SBL,fontWeight:700};

  useEffect(()=>{
    const goOnline=()=>{
      setIsOnline(true);
      // Sync any queued offline submissions
      try{
        const q=JSON.parse(localStorage.getItem("bac_offline")||"[]");
        if(q.length>0){
          const ex=JSON.parse(localStorage.getItem("bac_shifts")||"[]");
          q.forEach(r=>{
            ex.push({...r,synced:true});
            // Push each queued shift to Supabase
            pushShift(r, r._entries||[]);
          });
          localStorage.setItem("bac_shifts",JSON.stringify(ex));
          localStorage.setItem("bac_offline","[]");
          setOfflineQueue([]);
        }
      }catch(e){}
    };
    const goOffline=()=>setIsOnline(false);
    window.addEventListener("online",goOnline);
    window.addEventListener("offline",goOffline);
    return()=>{window.removeEventListener("online",goOnline);window.removeEventListener("offline",goOffline);};
  },[]);

  useEffect(()=>{
    try{if(name)localStorage.setItem("bac_name",name);}catch(e){}
  },[name]);

  useEffect(()=>{
    try{if(workerId)localStorage.setItem("bac_worker_id",workerId);}catch(e){}
  },[workerId]);

  useEffect(()=>{
    try{if(workerRole)localStorage.setItem("bac_worker_role",workerRole);}catch(e){}
  },[workerRole]);

  useEffect(()=>{
    const iv=setInterval(()=>setNowStr(new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})),1000);
    return()=>clearInterval(iv);
  },[]);

  useEffect(()=>{
    if((sc==="sheet"&&!fromSummary)||(sc==="home"&&tIn&&!tOut)) tmRef.current=setInterval(()=>setElapsed(s=>s+1),1000);
    else clearInterval(tmRef.current);
    return()=>clearInterval(tmRef.current);
  },[sc,fromSummary,tIn,tOut]);

  useEffect(()=>{
    const upd=()=>{if(wrapRef.current){const w=wrapRef.current.clientWidth-32;setScale(Math.min(1,w/SHEET_W));}};
    upd();window.addEventListener("resize",upd);return()=>window.removeEventListener("resize",upd);
  },[]);

  useEffect(()=>{
    if(sc==="home") setPastShifts(getShifts().slice(0,5));
  },[sc]);

  const getGPS=cb=>{
    if(!navigator.geolocation){cb(null,"GPS not available");return;}
    navigator.geolocation.getCurrentPosition(
      p=>cb({lat:p.coords.latitude.toFixed(5),lng:p.coords.longitude.toFixed(5),acc:Math.round(p.coords.accuracy)}),
      ()=>cb(null,"GPS unavailable - will work on live site"),
      {enableHighAccuracy:true,timeout:10000,maximumAge:0}
    );
  };

  const selSite=s=>{
    setSite(s);setCtr(CT[s]||"");
    setRecent(p=>[s,...p.filter(x=>x!==s)].slice(0,3));
    setTIn(new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}));
    setElapsed(0);setShowS(false);setSrch("");
    setGpsIn(null);setGpsOut(null);setGpsErr("");
    setSubmitted(false);setFromSummary(false);setSc("home");
    getGPS((pos,err)=>{
      if(pos)setGpsIn(pos);else setGpsErr(err||"");
      // Push clock-in to Supabase (fire and forget)
      pushClockEvent("in", workerId, name, s, pos);
    });
  };

  const clockOut=()=>{
    setTOut(new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}));
    getGPS((pos,err)=>{
      if(pos)setGpsOut(pos);else setGpsErr(err||"");
      // Push clock-out to Supabase (fire and forget)
      pushClockEvent("out", workerId, name, site, pos);
    });
    setFromSummary(false);
    setSc("summary");
  };

  // Go back to edit entries - keeps tOut, GPS, submitted state intact
  const goEdit=()=>{
    setFromSummary(true);
    setSc("sheet");
  };

  // Return to summary after editing entries
  const backToSummary=()=>{
    setFromSummary(false);
    setSc("summary");
  };

  const wordsToNum=t=>{
    const W={"zero":0,"one":1,"two":2,"to":2,"too":2,"three":3,"four":4,"for":4,"five":5,"six":6,"seven":7,"eight":8,"nine":9,"ten":10,"eleven":11,"twelve":12,"thirteen":13,"fourteen":14,"fifteen":15,"sixteen":16,"seventeen":17,"eighteen":18,"nineteen":19,"twenty":20,"thirty":30,"forty":40,"fifty":50,"sixty":60,"seventy":70,"eighty":80,"ninety":90,"hundred":100};
    // Replace spoken digit sequences like "one two zero" -> "120"
    let s=t;
    // Handle "one hundred and twenty" style
    s=s.replace(/(\w+)\s+hundred(?:\s+and\s+(\w+))?/gi,(m,h,r)=>{
      const hv=(W[h.toLowerCase()]||0)*100;
      const rv=r?(W[r.toLowerCase()]||0):0;
      return hv+rv;
    });
    // Replace individual spoken numbers
    s=s.replace(/(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)\s+(one|two|to|too|three|four|for|five|six|seven|eight|nine)/gi,(m,t,u)=>(W[t.toLowerCase()]||0)+(W[u.toLowerCase()]||0));
    s=s.replace(/(zero|one|two|to|too|three|four|for|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)/gi,m=>W[m.toLowerCase()]!==undefined?W[m.toLowerCase()]:m);
    return s;
  };

  const extractTime=t=>{
    // Returns minutes from a string fragment like "15 minutes", "1 hour", "2 hours 30 minutes", "half an hour"
    let mins=0;
    const hrMin=t.match(/(\d+)\s*(?:hours?|hr)\s*(?:and\s*)?(\d+)\s*(?:minutes?|mins?)/i);
    const hrOnly=t.match(/(\d+)\s*(?:hours?|hr)/i);
    const minOnly=t.match(/(\d+)\s*(?:minutes?|mins?)/i);
    const half=t.match(/half\s*(?:an?\s*)?hour/i);
    const quarter=t.match(/quarter\s*(?:of\s*an?\s*)?hour/i);
    if(hrMin){mins=parseInt(hrMin[1])*60+parseInt(hrMin[2]);}
    else if(hrOnly){mins=parseInt(hrOnly[1])*60;}
    else if(minOnly){mins=parseInt(minOnly[1]);}
    else if(half){mins=30;}
    else if(quarter){mins=15;}
    return mins;
  };

  const fixWords=t=>{
    const fixes=[
      // Rub down mishears (very comprehensive - browser mishears often)
      [/\brope down\b/gi,"rub down"],[/\block down\b/gi,"rub down"],[/\bbreak down\b/gi,"rub down"],
      [/\brub-down\b/gi,"rub down"],[/\brubb?ing down\b/gi,"rub down"],[/\bruff down\b/gi,"rub down"],
      [/\brupt down\b/gi,"rub down"],[/\brun down\b/gi,"rub down"],[/\brut down\b/gi,"rub down"],
      [/\bbroke down\b/gi,"rub down"],[/\blook down\b/gi,"rub down"],[/\bhook down\b/gi,"rub down"],
      [/\brob down\b/gi,"rub down"],[/\brop down\b/gi,"rub down"],[/\brubdown\b/gi,"rub down"],
      // Top coat / float coat mishears
      [/\btop coat\b/gi,"top coat"],[/\btopcoat\b/gi,"top coat"],[/\b1st coat\b/gi,"1st coat"],
      [/\bfirst coat\b/gi,"1st coat"],[/\bsecond coat\b/gi,"2nd coat"],[/\bfinal coat\b/gi,"final coat"],
      [/\bflat coat\b/gi,"float coat"],[/\bflute coat\b/gi,"float coat"],
      // Float mishears
      [/\bfloat out\b/gi,"float out"],[/\bfloated\b/gi,"floated"],[/\bfloating\b/gi,"floating"],
      [/\bflout\b/gi,"float"],[/\bflood\b/gi,"float"],[/\bflote\b/gi,"float"],
      // Make good / MG mishears
      [/\bmake good\b/gi,"make good"],[/\bmake-good\b/gi,"make good"],[/\bmaking good\b/gi,"make good"],
      [/\bmgg\b/gi,"make good"],[/\b\bmg\b\b/gi,"make good"],[/\bm\.g\.\b/gi,"make good"],
      [/\bfinish mg\b/gi,"finish make good"],[/\bfinish mgg\b/gi,"finish make good"],
      [/\bmake goods\b/gi,"make good"],[/\bmate good\b/gi,"make good"],[/\bmace good\b/gi,"make good"],
      // TJ / tape and joint
      [/\btj\b/gi,"TJ"],[/\btape and joint\b/gi,"TJ"],[/\btape joint\b/gi,"TJ"],
      [/\bteejay\b/gi,"TJ"],[/\btee jay\b/gi,"TJ"],
      // Nail off mishears
      [/\bnailer\b/gi,"nail off"],[/\bnail of\b/gi,"nail off"],[/\bnailed off\b/gi,"nail off"],
      [/\bnailing off\b/gi,"nail off"],[/\bnale off\b/gi,"nail off"],
      // Channel mishears
      [/\bchannel down\b/gi,"channel down"],[/\bchannels\b/gi,"channels"],[/\bchannelling\b/gi,"channelling"],
      [/\bcannel\b/gi,"channel"],[/\bchanel\b/gi,"channel"],
      // Skim mishears
      [/\bskim coat\b/gi,"skim coat"],[/\bskim over\b/gi,"skim over"],[/\bskimming\b/gi,"skimming"],
      [/\bskim off\b/gi,"skim off"],[/\bscheme\b/gi,"skim"],[/\bscam\b/gi,"skim"],
      // Board out mishears
      [/\bbored out\b/gi,"board out"],[/\bbolt out\b/gi,"board out"],[/\bboarded out\b/gi,"board out"],
      [/\bboarding out\b/gi,"board out"],
      // Dot and dab mishears
      [/\bdot in dab\b/gi,"dot and dab"],[/\bdot n dab\b/gi,"dot and dab"],[/\bdot dab\b/gi,"dot and dab"],
      // Patch mishears
      [/\bpatching\b/gi,"patching"],[/\bpatch up\b/gi,"patch up"],[/\bpatched\b/gi,"patched"],
      [/\bpash\b/gi,"patch"],[/\bpatch's\b/gi,"patches"],
      // Clean up
      [/\bclean up\b/gi,"clean up"],[/\bcleaned up\b/gi,"clean up"],[/\bcleaning up\b/gi,"clean up"],
      [/\bclean-up\b/gi,"clean up"],
      // Cupboard mishears
      [/\bcupboad\b/gi,"cupboard"],[/\bcupbord\b/gi,"cupboard"],[/\bcupbored\b/gi,"cupboard"],
      // Crack/cracks mishears
      [/\bcracks?\b/gi,"cracks"],[/\bcrack in\b/gi,"crack in"],[/\bcracking\b/gi,"cracking"],
      // Number corrections (when used as counts not times)
      [/\bfree patches\b/gi,"3 patches"],[/\bfree coats?\b/gi,"3 coats"],
      [/\bto patches\b/gi,"2 patches"],[/\bto coats?\b/gi,"2 coats"],
      [/\bfor patches\b/gi,"4 patches"],[/\bfor coats?\b/gi,"4 coats"],
      // Plasterboard
      [/\bplaster board\b/gi,"plasterboard"],[/\bplasterboarding\b/gi,"plasterboarding"],
      // Areas / rooms
      [/\bup stair[s]?\b/gi,"upstairs"],[/\bdown stair[s]?\b/gi,"downstairs"],
      [/\blounge\b/gi,"lounge"],[/\bbedroom[s]?\b/gi,"bedrooms"],[/\bceil+ing\b/gi,"ceiling"],
    ];
    let s=t;
    fixes.forEach(([from,to])=>{s=s.replace(from,to);});
    return s;
  };

  const parseFullSpeech=raw=>{
    // Apply word corrections first
    let corrected=fixWords(raw);
    
    // Extract plot number - "plot one", "plot 120", etc
    let plotNum="";
    const W={"zero":0,"one":1,"two":2,"to":2,"too":2,"three":3,"four":4,"for":4,"five":5,"six":6,"seven":7,"eight":8,"nine":9,"ten":10,"eleven":11,"twelve":12,"thirteen":13,"fourteen":14,"fifteen":15,"sixteen":16,"seventeen":17,"eighteen":18,"nineteen":19,"twenty":20,"thirty":30,"forty":40,"fifty":50,"sixty":60,"seventy":70,"eighty":80,"ninety":90,"hundred":100};
    
    
    if(!plot){  // ← ONLY extract plot if field is empty
    // Try "plot one/two/three" style first
    const plotWord=corrected.match(/\b(?:plot|plt)\s+(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|zero|to|too|for)\b/i);
    if(plotWord){
      plotNum=String(W[plotWord[1].toLowerCase()]);
      corrected=corrected.replace(plotWord[0],"");
    } else {
      // Try "plot 120" numeric style
      const numPlot=corrected.match(/\b(?:plot|plt)\s*(\d+)\b/i);
      if(numPlot){
        plotNum=numPlot[1];
        corrected=corrected.replace(numPlot[0],"");
      }
    }
    }
    
    // NOW convert rest to digits for time parsing
    let t=wordsToNum(corrected.toLowerCase());

    // Find and extract time expression - match the longest possible time string
    let totalMins=0, timeStr="";
    const patterns=[
      /(\d+)\s*(?:hours?|hrs?|h)\s*(?:and\s*)?(\d+)\s*(?:minutes?|mins?|m)(?![a-z])/i,
      /(\d+)\s*(?:hours?|hrs?|h)(?![a-z])/i,
      /(\d+)\s*(?:minutes?|mins?|m)(?![a-z])/i,
      /half\s*(?:an?\s*)?hour/i,
      /quarter\s*(?:of\s*an?\s*)?hour/i
    ];
    for(const p of patterns){
      const m=t.match(p);
      if(m){timeStr=m[0];totalMins=pMin(m[0]);break;}
    }
    if(timeStr)t=t.replace(timeStr,"");

    // If no explicit plot number, try leading number as plot (but only AFTER time is removed)
    if(!plotNum){
      const leading=t.match(/^\s*(\d+)\b/);
      if(leading){plotNum=leading[1];t=t.replace(leading[0],"");}
    }

    // Clean up the rest as description
    let taskStr=t.replace(/,\s*,/g,",").replace(/^\s*,|,\s*$/g,"").replace(/\s+/g," ").trim();
    const descStr=taskStr?taskStr.charAt(0).toUpperCase()+taskStr.slice(1):"";

    return {plotNum, descStr, totalMins};
  };

  const voiceEntry=()=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){alert("Voice not supported in this browser — please type the details");return;}
    if(listen&&lisF==="entry"){
      if(window._bacSR){try{window._bacSR.stop();}catch(e){}}
      return;
    }
    try{
      const r=new SR();
      r.lang="en-GB";
      r.continuous=false;  // Don't keep listening - one speech then stop
      r.interimResults=true;
      let finalTranscript="";
      let interimTranscript="";
      r.onstart=()=>{setListen(true);setLisF("entry");setVoiceStatus("🔴 Listening — speak now");finalTranscript="";interimTranscript="";};
      r.onresult=e=>{
        // Rebuild transcripts from scratch each time to avoid duplication
        finalTranscript="";
        interimTranscript="";
        for(let i=0;i<e.results.length;i++){
          const transcript=e.results[i][0].transcript;
          if(e.results[i].isFinal){
            finalTranscript+=transcript+" ";
          } else {
            interimTranscript+=transcript;
          }
        }
        setVoiceStatus("🔴 "+(finalTranscript+interimTranscript).trim());
      };
      r.onerror=e=>{
        setListen(false);setLisF(null);
        if(e.error==="not-allowed") alert("Microphone blocked — allow microphone in browser settings");
        else if(e.error!=="aborted") alert("Voice error: "+e.error);
      };
      r.onend=()=>{
        setListen(false);setLisF(null);
        const t=finalTranscript.trim();
        if(t){
          const parsed=parseFullSpeech(t);
          // Only update plot if field is empty AND we got a plot number
          if(parsed.plotNum && !plot) setPlot(parsed.plotNum);
          if(parsed.descStr) setDesc(p=>p?p+", "+parsed.descStr:parsed.descStr);
          if(parsed.totalMins>0) setTSpent(fMin(parsed.totalMins));
          setVoiceStatus("✅ "+t);
        } else {
          setVoiceStatus("");
        }
      };
      r.start();
      window._bacSR=r;
    }catch(e){alert("Voice not available — please type the details");}
  };

  const voice=field=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){alert("Voice works best in Chrome");return;}
    // Use activeField to route to the correct input if one is focused
    const target=activeField||field;
    const r=new SR();r.lang="en-GB";r.continuous=false;r.interimResults=false;
    r.onstart=()=>{setListen(true);setLisF(target);};
    r.onresult=e=>{
      const t=e.results[0][0].transcript;
      if(target==="plot") setPlot(t.trim());
      else if(target==="time"){const m=extractTime(wordsToNum(t.toLowerCase()));if(m>0)setTSpent(fMin(m));else setTSpent(t.trim());}
      else if(target==="desc") setDesc(p=>p?p+", "+t:t);
      else if(target==="mat") setMats(p=>p?p+" "+t:t);
    };
    r.onend=()=>{setListen(false);setLisF(null);};r.start();
  };

  const addE=()=>{if(!plot||!desc)return;setEntries(p=>[...p,{id:Date.now(),plot:plot.trim(),desc,tt:tSpent,mins:pMin(tSpent),sec}]);setPlot("");setDesc("");setTSpent("");};
  const newDay=()=>{setTIn("");setTOut("");setEntries([]);setMats("");setSite("");setCtr("");setElapsed(0);setGpsIn(null);setGpsOut(null);setGpsErr("");setSubmitted(false);setFromSummary(false);setPdfUrl(null);setSc("home");};

  const buildWeekSummary=()=>{
    const all=getShifts();
    const now=new Date();
    const day=now.getDay();
    const diffToMon=day===0?-6:1-day;
    const mon=new Date(now);mon.setDate(now.getDate()+diffToMon);mon.setHours(0,0,0,0);
    const sun=new Date(mon);sun.setDate(mon.getDate()+6);sun.setHours(23,59,59,999);
    const weekShifts=all.filter(s=>{
      const parts=s.date.split("/");
      const sd=new Date(parts[2],parts[1]-1,parts[0]);
      return sd>=mon&&sd<=sun;
    });
    if(weekShifts.length===0){alert("No shifts this week yet");return;}
    const days={};
    weekShifts.forEach(s=>{if(!days[s.date])days[s.date]={date:s.date,shifts:[]};days[s.date].shifts.push(s);});
    const totalMins=weekShifts.reduce((a,s)=>a+(s.totalMins||0),0);
    const weekLabel=mon.toLocaleDateString("en-GB",{day:"numeric",month:"short"})+" - "+sun.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
    setWeekSummary({weekLabel,days,totalMins,shifts:weekShifts});
  };

  const applyQuickEntry=()=>{
    if(!quickEntry.trim())return;
    const parsed=parseFullSpeech(quickEntry);
    const p=parsed.plotNum||plot;
    const d=parsed.descStr||desc;
    const m=parsed.totalMins||pMin(tSpent);
    if(!p){alert("Please start with the plot number e.g. 120, rub down, 15 minutes");return;}
    if(!d){alert("Please include what was done");return;}
    setEntries(prev=>[...prev,{id:Date.now(),plot:p.trim(),desc:d,tt:fMin(m),mins:m,sec}]);
    setPlot("");setDesc("");setTSpent("");setQuickEntry("");setShowQuick(false);
  };

  const submitShift=()=>{
    const record={id:Date.now(),workerId,name,site,date:today,tIn,tOut,gpsIn:gpsIn||null,gpsOut:gpsOut||null,mgH,siH,totalMins:mgM+siM,submitted:new Date().toISOString()};
    if(isOnline){
      if(saveShift(record)){
        setSubmitted(true);
        // Push to Supabase (fire and forget — localStorage is source of truth)
        pushShift(record, entries);
      }
      else alert("Could not save");
    } else {
      // Save to offline queue
      try{
        const q=JSON.parse(localStorage.getItem("bac_offline")||"[]");
        q.push({...record,_entries:entries});
        localStorage.setItem("bac_offline",JSON.stringify(q));
        setOfflineQueue(q);
        setSubmitted(true);
      }catch(e){alert("Could not save offline");}
    }
  };

  const openEdit=shift=>{
    setEditShift(shift);setEditIn(shift.tIn);setEditOut(shift.tOut);setEditNote("");setEditDone(false);setShowEdit(true);
  };

  const submitEdit=()=>{
    try{
      const all=getShifts();
      const idx=all.findIndex(s=>s.id===editShift.id);
      if(idx>=0){all[idx]={...all[idx],tIn:editIn,tOut:editOut,editNote,pending:true,edited:new Date().toISOString()};}
      localStorage.setItem("bac_shifts",JSON.stringify(all));
      // Push edit request to Supabase
      pushRequest({
        workerId,
        workerName:name,
        type:"time_edit",
        note:"Edit shift "+editShift.date+" at "+(LC[editShift.site]||editShift.site)+(editNote?" — "+editNote:""),
        tIn:editIn,
        tOut:editOut
      });
      setEditDone(true);setPastShifts(getShifts().slice(0,5));
    }catch(e){alert("Could not save edit");}
  };

  const submitAdd=()=>{
    if(!addSite||!addDate||!addIn||!addOut){alert("Please fill in all fields");return;}
    const record={id:Date.now(),workerId,name,site:addSite,date:addDate,tIn:addIn,tOut:addOut,gpsIn:null,gpsOut:null,mgH:"",siH:"",totalMins:0,addNote,pending:true,manualEntry:true,submitted:new Date().toISOString()};
    if(saveShift(record)){
      // Push manual add request to Supabase
      pushRequest({
        workerId,
        workerName:name,
        type:"missed_clock",
        note:"Add shift "+addDate+" at "+addSite+" ("+addIn+"–"+addOut+")"+(addNote?" — "+addNote:""),
        tIn:addIn,
        tOut:addOut
      });
      setAddDone(true);setPastShifts(getShifts().slice(0,5));
    }
    else alert("Could not save shift");
  };

  const mgE=entries.filter(e=>e.sec==="mg"),siE=entries.filter(e=>e.sec==="si");
  const mgG=gPlot(mgE),siG=gPlot(siE);
  const mgM=mgE.reduce((a,e)=>a+e.mins,0),siM=siE.reduce((a,e)=>a+e.mins,0);
  const mgH=mgM?(mgM/60).toFixed(1).replace(/\.0$/,""):"";
  const siH=siM?(siM/60).toFixed(1).replace(/\.0$/,""):"";
  const mgTxt=mgG.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
  const siTxt=siG.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
  const filt=SITES.filter(s=>s.toLowerCase().includes(srch.toLowerCase()));
  const addFilt=SITES.filter(s=>s.toLowerCase().includes(addSrch.toLowerCase()));

  const buildPDF=()=>{
    const rg=gPlot(entries.filter(e=>e.sec==="mg"));
    const sg=gPlot(entries.filter(e=>e.sec==="si"));
    const rt=rg.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
    const st=sg.map(g=>"PLT "+g.plot+" - "+g.items.map(i=>i.desc).join(", ")+(g.mins?" ("+fMin(g.mins)+")":"")).join("\n");
    const mh=entries.filter(e=>e.sec==="mg").reduce((a,e)=>a+e.mins,0);
    const sh=entries.filter(e=>e.sec==="si").reduce((a,e)=>a+e.mins,0);
    const mhStr=mh?(mh/60).toFixed(1).replace(/\.0$/,""):"";
    const shStr=sh?(sh/60).toFixed(1).replace(/\.0$/,""):"";
    const td="border:1px solid #444;padding:3px 6px;font-family:Arial,sans-serif;font-size:11px;color:#111;vertical-align:middle;";
    const th=td+"background:#BDD6F1;font-weight:700;";
    const html="<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>BAC Daywork Sheet</title>"
      +"<style>body{margin:20px;font-family:Arial,sans-serif;}table{border-collapse:collapse;width:100%;}"
      +"@media print{body{margin:0;}@page{margin:8mm;}.noprint{display:none;}}</style>"
      +"</head><body>"
      +"<div style=\"width:680px;border:1.5px solid #333;padding:4px 10px 6px;box-sizing:border-box;\">"
      +"<div style=\"display:flex;align-items:center;margin-bottom:4px;gap:12px;\">"
      +"<img src=\""+LOGO+"\" style=\"width:56px;height:56px;object-fit:contain;\" alt=\"BAC\"/>"
      +"<div style=\"flex:1;text-align:center;font-weight:900;font-size:16px;letter-spacing:2px;text-decoration:underline;\">DAYWORK SHEET</div>"
      +"</div>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td rowspan=\"3\" style=\""+th+"width:18%;text-align:center;font-size:9px;\">SUB-CONTRACTOR NAME</td>"
      +"<td rowspan=\"3\" style=\""+td+"width:25%;font-size:13px;font-weight:700;text-align:center;\">"+name+"</td>"
      +"<td style=\""+th+"width:19%;font-size:9px;\">DATE</td><td style=\""+td+"font-size:11px;\">"+today+"</td></tr>"
      +"<tr><td style=\""+th+"font-size:9px;\">SITE LOCATION</td><td style=\""+td+"font-size:11px;\">"+(LC[site]||site)+"</td></tr>"
      +"<tr><td style=\""+th+"font-size:9px;\">CONTRACTOR</td><td style=\""+td+"font-size:11px;\">"+ctr+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td colspan=\"2\" style=\""+th+"font-size:10px;\">Work Sheet - Please Include Plot Numbers</td></tr>"
      +"<tr><td style=\""+td+"font-size:9px;color:#CC0000;font-weight:700;font-style:italic;\">Please confirm how long you spent in each plot next to your plot number</td>"
      +"<td style=\""+td+"width:75px;text-align:right;font-weight:700;font-size:10px;\">Hours: "+mhStr+"</td></tr>"
      +"<tr><td colspan=\"2\" style=\""+td+"height:380px;vertical-align:top;padding-top:5px;white-space:pre-wrap;\">"+rt+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td style=\""+th+"font-size:10px;\">Site Instruction Works - Please Include Plot Numbers</td>"
      +"<td style=\""+th+"width:75px;text-align:right;font-size:10px;\">Hours: "+shStr+"</td></tr>"
      +"<tr><td colspan=\"2\" style=\""+td+"height:140px;vertical-align:top;padding-top:5px;white-space:pre-wrap;\">"+st+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"margin-bottom:2px\"><tbody>"
      +"<tr><td style=\""+th+"font-size:10px;\">Materials/Plant/Access Equipment Used</td></tr>"
      +"<tr><td style=\""+td+"height:26px;vertical-align:top;font-size:11px;\">"+mats+"</td></tr>"
      +"</tbody></table>"
      +"<table style=\"width:45%;margin-bottom:2px\"><tbody>"
      +"<tr><td style=\""+th+"text-align:center;width:50%;font-size:10px;\">TIME IN</td><td style=\""+th+"text-align:center;width:50%;font-size:10px;\">TIME OUT</td></tr>"
      +"<tr><td style=\""+td+"text-align:center;font-size:14px;font-weight:700;\">"+tIn+"</td><td style=\""+td+"text-align:center;font-size:14px;font-weight:700;\">"+tOut+"</td></tr>"
      +"</tbody></table>"
      +"<table><tbody><tr>"
      +"<td style=\""+th+"width:28%;font-size:9px;\">BAC Drywall Supervisor Signature</td>"
      +"<td style=\""+td+"width:20%;padding:10px;\">&nbsp;</td>"
      +"<td style=\""+th+"width:10%;font-size:9px;\">Date</td>"
      +"<td style=\""+td+"width:42%;padding:10px;\">&nbsp;</td>"
      +"</tr></tbody></table>"
      +"</div>"
      +"<br><p class=\"noprint\" style=\"color:#666;font-size:13px;\">To save as PDF: tap the share/menu button in your browser → Print → Save as PDF</p>"
      +"</body></html>";
    const encoded="data:text/html;charset=utf-8,"+encodeURIComponent(html);
    setPdfUrl(encoded);
  };


  // ── LOGIN HANDLERS ──
  const signOut=()=>{
    if(tIn&&!tOut){alert("Please clock out before signing out");return;}
    try{
      localStorage.removeItem("bac_name");
      localStorage.removeItem("bac_worker_id");
      localStorage.removeItem("bac_worker_role");
    }catch(e){}
    setName("");setWorkerId("");setWorkerRole("");setShowSettings(false);
    setSc("home");setSite("");setCtr("");setTIn("");setTOut("");setEntries([]);setMats("");
  };

  const savePinChange=async()=>{
    if(newPin1.length<4){setPinChangeError("PIN must be at least 4 digits");return;}
    if(newPin1!==newPin2){setPinChangeError("PINs don't match — try again");return;}
    const ok=await updateWorkerPin(workerId,newPin1);
    if(!ok){setPinChangeError("Could not save — check connection and try again");return;}
    setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);
    setShowPinChange(false);setShowPinPrompt(false);
    alert("PIN changed. Use your new PIN next time you sign in.");
  };

  const keepTempPin=()=>{
    // They chose to keep the temp PIN. Just dismiss the prompt - it'll show again next sign-in.
    setShowPinPrompt(false);
  };

  // PIN keypad button — used in login + change screens
  const PinPad=({value,onPress,onClear,onBack})=>{
    const btn={padding:"18px 0",background:W,border:"1.5px solid "+BD,borderRadius:14,fontSize:24,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK};
    return (
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,maxWidth:280,margin:"0 auto"}}>
        {[1,2,3,4,5,6,7,8,9].map(n=><button key={n} style={btn} onClick={()=>onPress(String(n))}>{n}</button>)}
        <button style={{...btn,background:"#F2F2F7",border:"none",fontSize:14}} onClick={onClear}>Clear</button>
        <button style={btn} onClick={()=>onPress("0")}>0</button>
        <button style={{...btn,background:"#F2F2F7",border:"none",fontSize:18}} onClick={onBack}>⌫</button>
      </div>
    );
  };

  // Render PIN dots
  const pinDots=(val,len=6)=>{
    const dots=[];
    for(let i=0;i<len;i++){
      dots.push(<div key={i} style={{width:14,height:14,borderRadius:7,background:i<val.length?BL:"transparent",border:"2px solid "+(i<val.length?BL:BD),margin:"0 6px"}}/>);
    }
    return <div style={{display:"flex",justifyContent:"center",alignItems:"center",margin:"24px 0"}}>{dots}</div>;
  };

  // ── If not logged in, show login screen and bail out before main UI ──
  if(!workerId){
    return (
      <div style={{minHeight:"100vh",background:BG,fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",color:DK,display:"flex",flexDirection:"column"}}>
        <div style={{padding:"32px 16px 0",textAlign:"center"}}>
          <img src={LOGO} style={{width:80,height:80,objectFit:"contain",marginBottom:8}} alt="BAC Drywall"/>
          <div style={{fontWeight:800,fontSize:22,marginBottom:4}}>{COMPANY_NAME}</div>
          <div style={{fontSize:13,color:MT,marginBottom:24}}>Enter your PIN to sign in</div>
        </div>
        <div style={{flex:1,padding:"0 24px",maxWidth:380,margin:"0 auto",width:"100%",boxSizing:"border-box"}}>
          {pinDots(pinInput,4)}
          {pinError && <div style={{textAlign:"center",color:RD,fontSize:13,fontWeight:600,marginBottom:14}}>{pinError}</div>}
          {pinChecking && <div style={{textAlign:"center",color:MT,fontSize:13,marginBottom:14}}>Checking…</div>}
          <PinPad
            value={pinInput}
            onPress={d=>{
              if(pinInput.length>=4)return;
              const next=pinInput+d;
              setPinInput(next);setPinError("");
              if(next.length===4){
                setTimeout(()=>{
                  (async()=>{
                    setPinChecking(true);setPinError("");
                    const w=await loginByPin(next);
                    setPinChecking(false);
                    if(!w){setPinError("PIN not recognised. Try again or ask the office.");setPinInput("");return;}
                    setWorkerId(w.id);setName(w.name);setWorkerRole(w.role||"worker");
                    setPinInput("");
                    if(w.pin_temporary)setShowPinPrompt(true);
                  })();
                },200);
              }
            }}
            onClear={()=>{setPinInput("");setPinError("");}}
            onBack={()=>{setPinInput(p=>p.slice(0,-1));setPinError("");}}
          />
          <div style={{textAlign:"center",fontSize:12,color:MT,marginTop:32,padding:"0 20px"}}>
            Forgot your PIN? Ask the office to reset it for you.
          </div>
        </div>
        <div style={{padding:"12px 16px",textAlign:"center",fontSize:11,color:MT}}>{BUILD}</div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:BG,fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",color:DK}}>
      <style>{"@media print{#ui{display:none!important;}#ps{display:block!important;position:absolute;top:0;left:0;width:100%;}}"}</style>
      <div id="ps" style={{display:"none"}}>
        <div style={{width:SHEET_W,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
          <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
            <div style={{flexShrink:0,width:56,height:56}}><img src={LOGO} alt="BAC" style={{width:"100%",height:"100%",objectFit:"contain",objectPosition:"left center",display:"block"}}/></div>
            <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span></div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr>
              <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
              <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{name}</td>
              <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
              <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{today}</td>
            </tr>
            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{LC[site]||site}</td></tr>
            <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{ctr}</td></tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr><td colSpan={2} style={{...CB,fontSize:10,padding:"1px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
            <tr>
              <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
              <td style={{...C,width:75,textAlign:"right",fontWeight:700,fontSize:10,whiteSpace:"nowrap",padding:"1px 4px"}}>Hours: {mgH}</td>
            </tr>
            <tr><td colSpan={2} style={{...C,height:380,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{mgTxt}</td></tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr>
              <td style={{...CB,fontSize:10,padding:"1px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
              <td style={{...CB,width:75,textAlign:"right",fontSize:10,whiteSpace:"nowrap",padding:"1px 4px"}}>Hours: {siH}</td>
            </tr>
            <tr><td colSpan={2} style={{...C,height:140,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{siTxt}</td></tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr><td style={{...CB,fontSize:10,padding:"1px 4px"}}>Materials/Plant/Access Equipment Used</td></tr>
            <tr><td style={{...C,height:26,verticalAlign:"top",paddingTop:2,fontSize:11}}>{mats}</td></tr>
          </tbody></table>
          <table style={{width:"45%",borderCollapse:"collapse",marginBottom:2}}><tbody>
            <tr>
              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME IN</td>
              <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME OUT</td>
            </tr>
            <tr>
              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tIn}</td>
              <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tOut}</td>
            </tr>
          </tbody></table>
          <table style={{width:"100%",borderCollapse:"collapse"}}><tbody>
            <tr>
              <td style={{...CB,width:"28%",fontSize:9,padding:"2px 4px"}}>BAC Drywall Supervisor Signature</td>
              <td style={{...C,width:"20%",padding:"2px 4px"}}>&nbsp;</td>
              <td style={{...CB,width:"10%",fontSize:9,padding:"2px 4px"}}>Date</td>
              <td style={{...C,width:"42%",padding:"2px 4px"}}>&nbsp;</td>
            </tr>
          </tbody></table>
        </div>
      </div>
      <div id="ui">
        <div style={{background:W,padding:"6px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+BD,position:"sticky",top:0,zIndex:100}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <img src={LOGO} style={{width:44,height:44,objectFit:"contain"}} alt="BAC Drywall"/>
            <span style={{fontWeight:800,fontSize:18}}>{COMPANY_NAME}</span>
            <span style={{fontSize:11,color:MT,fontWeight:600,marginLeft:2}}>{BUILD}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {!isOnline&&<span style={{background:RD,color:W,fontSize:11,fontWeight:700,padding:"3px 7px",borderRadius:8}}>📵 Offline</span>}
            {offlineQueue.length>0&&isOnline&&<span style={{background:"#F59E0B",color:W,fontSize:11,fontWeight:700,padding:"3px 7px",borderRadius:8}}>⟳ {offlineQueue.length} syncing</span>}
            <span style={{fontSize:16,fontWeight:800,color:BL}}>{nowStr}</span>
            <button onClick={()=>setShowSettings(true)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,padding:"4px 6px"}} aria-label="Settings">⚙️</button>
          </div>
        </div>

        <div ref={wrapRef} style={{maxWidth:540,margin:"0 auto",padding:"16px 16px 80px"}}>

          {sc==="home" && (
            <div>
              <div style={{marginBottom:16,fontSize:22,fontWeight:800}}>{greet}{name?", "+name.split(" ")[0]:""} 👋</div>
              {tIn && !tOut && (
                <div>
                  <div style={{...card,background:LBL,border:"1px solid #BDD6F5",padding:16,marginBottom:12}}>
                    <div style={{fontSize:40,fontWeight:800,color:BL,letterSpacing:2}}>{fSec(elapsed)}</div>
                    <div style={{fontSize:14,color:BL,marginTop:4,fontWeight:600}}>{site} · Started {tIn}</div>
                    {gpsIn ? <div style={{fontSize:11,color:GR,marginTop:6}}>📍 GPS locked · ±{gpsIn.acc}m</div> : <div style={{fontSize:11,color:"#999",marginTop:6}}>{gpsErr?"📍 GPS will work on live site":"📍 Getting GPS..."}</div>}
                    <button onClick={clockOut} style={{marginTop:14,width:"100%",padding:13,background:W,color:RD,border:"1.5px solid "+RD,borderRadius:10,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Clock Out</button>
                  </div>
                  <div style={{display:"flex",gap:10,marginBottom:12}}>
                    <button onClick={()=>setSc("sheet")} style={{flex:1,background:W,border:"2px solid "+BL,borderRadius:14,padding:"12px 10px",display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 1px 3px rgba(0,0,0,.08)"}}>
                      <span style={{fontSize:20}}>📋</span>
                      <div><div style={{fontWeight:800,fontSize:13,color:BL}}>Timesheet</div><div style={{fontSize:11,color:MT}}>{entries.length>0?entries.length+" entries":"Log plot entries"}</div></div>
                    </button>
                    <button onClick={()=>{setAddSite("");setAddDate(todayISO);setAddIn("");setAddOut("");setAddNote("");setAddDone(false);setShowAdd(true);}} style={{flex:1,background:W,border:"1.5px solid "+BD,borderRadius:14,padding:"12px 10px",display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 1px 3px rgba(0,0,0,.08)"}}>
                      <span style={{fontSize:20}}>📩</span>
                      <div><div style={{fontWeight:800,fontSize:13,color:DK}}>My Requests</div><div style={{fontSize:11,color:MT}}>Time edits & issues</div></div>
                    </button>
                  </div>
                </div>
              )}
              {!tIn && (
                <div style={{...card,background:LBL,border:"1px solid #BDD6F5",padding:14}}>
                  <div style={{marginBottom:12,fontSize:13,color:BL,fontWeight:600}}>Signed in as <span style={{fontWeight:800}}>{name}</span></div>
                  <button onClick={()=>setShowS(true)} style={{width:"100%",padding:16,background:BL,color:W,border:"none",borderRadius:12,fontSize:18,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 12px rgba(47,128,237,.35)",display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
                    <span style={{fontSize:20}}>⏱</span> Clock In
                  </button>
                </div>
              )}
              <button onClick={buildWeekSummary} style={{width:"100%",padding:14,background:W,border:"1.5px solid "+BD,borderRadius:14,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"center",gap:8,color:DK}}>
                <span>📋</span> This Week's Summary
              </button>

              <div style={card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontWeight:800,fontSize:15}}>Recent Shifts</div>
                  <button onClick={()=>{setAddSite("");setAddDate(todayISO);setAddIn("");setAddOut("");setAddNote("");setAddDone(false);setShowAdd(true);}} style={{background:LBL,border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,fontWeight:700,color:BL,cursor:"pointer",fontFamily:"inherit"}}>+ Add Shift</button>
                </div>
                {pastShifts.length===0 && <div style={{textAlign:"center",color:MT,fontSize:13,padding:"10px 0"}}>No shifts yet</div>}
                {pastShifts.map((s,i)=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingBottom:i<pastShifts.length-1?12:0,marginBottom:i<pastShifts.length-1?12:0,borderBottom:i<pastShifts.length-1?"1px solid "+BD:"none"}}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                        {s.pending && <span style={{background:"#FFF3CD",color:"#856404",fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6}}>Pending</span>}
                        {s.manualEntry && <span style={{background:"#E8F4FD",color:"#0c5460",fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6}}>Manual</span>}
                        <span style={{fontWeight:700,fontSize:13}}>{LC[s.site]||s.site}</span>
                      </div>
                      <div style={{fontSize:12,color:MT}}>{s.date} · {s.tIn}–{s.tOut}</div>
                    </div>
                    <button onClick={()=>openEdit(s)} style={{background:"#F2F2F7",border:"none",borderRadius:8,padding:"6px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK,flexShrink:0,marginLeft:8}}>✏️ Edit</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showS && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:W,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
                <div style={{padding:"16px 20px 12px",borderBottom:"1px solid "+BD}}>
                  <div style={{textAlign:"center",fontSize:18,fontWeight:700,marginBottom:12}}>Select Job Site</div>
                  <input style={{...inp,background:"#F2F2F7"}} placeholder="Search sites..." value={srch} onChange={e=>setSrch(e.target.value)}/>
                </div>
                <div style={{overflowY:"auto",flex:1}}>
                  {!srch && recent.length>0 && (
                    <div>
                      <div style={{padding:"10px 20px 4px",fontSize:11,color:MT,fontWeight:700,textTransform:"uppercase",letterSpacing:".8px"}}>Most Used</div>
                      {recent.map(r=>(
                        <button key={r} onClick={()=>selSite(r)} style={{width:"100%",padding:"14px 20px",background:W,border:"none",borderBottom:"1px solid "+BD,cursor:"pointer",display:"flex",alignItems:"center",gap:14,fontSize:16,color:DK,fontFamily:"inherit",textAlign:"left"}}>
                          <div style={{width:48,height:48,borderRadius:6,background:W,border:"2px solid "+DOTS[SITES.indexOf(r)%DOTS.length],flexShrink:0,overflow:"hidden",padding:2,boxSizing:"border-box"}}>
                            <img src={LOGO} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/>
                          </div>
                          <div style={{flex:1}}>{r}</div>
                          {PC[r] && <span style={{fontSize:12,color:MT,fontWeight:600}}>{PC[r]}</span>}
                        </button>
                      ))}
                      <div style={{padding:"10px 20px 4px",fontSize:11,color:MT,fontWeight:700,textTransform:"uppercase",letterSpacing:".8px"}}>All Sites</div>
                    </div>
                  )}
                  {filt.map(r=>(
                    <button key={r} onClick={()=>selSite(r)} style={{width:"100%",padding:"14px 20px",background:W,border:"none",borderBottom:"1px solid "+BD,cursor:"pointer",display:"flex",alignItems:"center",gap:14,fontSize:16,color:DK,fontFamily:"inherit",textAlign:"left"}}>
                      <div style={{width:48,height:48,borderRadius:6,background:W,border:"2px solid "+DOTS[SITES.indexOf(r)%DOTS.length],flexShrink:0,overflow:"hidden",padding:2,boxSizing:"border-box"}}>
                        <img src={LOGO} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/>
                      </div>
                      <div style={{flex:1}}>{r}</div>
                      {PC[r] && <span style={{fontSize:12,color:MT,fontWeight:600}}>{PC[r]}</span>}
                    </button>
                  ))}
                </div>
                <div style={{padding:"12px 20px 24px",borderTop:"1px solid "+BD}}>
                  <button onClick={()=>{setSite("Site Not Listed");setCtr("BAC Drywall");setTIn(new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}));setElapsed(0);setGpsIn(null);setGpsOut(null);setGpsErr("");setSubmitted(false);setFromSummary(false);setShowS(false);setSrch("");getGPS((pos,err)=>{if(pos)setGpsIn(pos);else setGpsErr(err||"");});setSiteRequestNote("");setSiteRequestSent(false);setShowSiteRequest(true);}} style={{width:"100%",padding:12,background:"#FFF8E1",border:"1.5px solid #F59E0B",borderRadius:12,fontSize:15,color:"#92400E",cursor:"pointer",fontFamily:"inherit",fontWeight:700,marginBottom:8}}>⚠️ My site isn't listed</button>
                  <button onClick={()=>{setShowS(false);setSrch("");}} style={{width:"100%",padding:14,background:"#F2F2F7",border:"none",borderRadius:12,fontSize:17,color:DK,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {sc==="sheet" && (
            <div>
              <div style={{background:LBL,border:"1px solid #BDD6F5",borderRadius:fromSummary?16:0,padding:"18px 16px",marginBottom:16,...(!fromSummary?{position:"sticky",top:50,zIndex:50,borderRadius:"0 0 16px 16px"}:{})}}>
                {fromSummary ? (
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:BL,marginBottom:4}}>Editing entries for</div>
                    <div style={{fontSize:14,color:BL,fontWeight:600,marginBottom:14}}>{site} · {tIn}–{tOut}</div>
                    <button onClick={backToSummary} style={{width:"100%",padding:13,background:GR,color:W,border:"none",borderRadius:10,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✅ Done — Back to Summary</button>
                  </div>
                ) : (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:BL}}>{site}</div>
                      <div style={{fontSize:12,color:MT,marginTop:1}}>{fSec(elapsed)} · Started {tIn}</div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>setSc("home")} style={{background:W,border:"1.5px solid "+BD,borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>‹ Home</button>
                      <button onClick={clockOut} style={{background:RD,border:"none",borderRadius:8,padding:"7px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:W}}>Clock Out</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[SHEET_SECTION_A,SHEET_SECTION_B].map(s=>(
                  <button key={s.key} onClick={()=>setSec(s.key)} style={{flex:1,padding:"10px 8px",border:"2px solid "+(sec===s.key?BL:BD),borderRadius:10,background:sec===s.key?LBL:W,color:sec===s.key?BL:MT,fontFamily:"inherit",fontWeight:700,fontSize:13,cursor:"pointer"}}>{s.tab}</button>
                ))}
              </div>
              <div style={{...card,border:"2px solid "+BL}}>
                <div style={{fontWeight:800,fontSize:15,color:BL,marginBottom:8}}>+ Add Plot Entry</div>
                {/* Plot number - full width */}
                <div style={{marginBottom:8}}>
                  <label style={lbl}>Plot No.</label>
                  <input style={{...inp,padding:"9px 8px"}} value={plot} onChange={e=>setPlot(e.target.value)} placeholder="120" inputMode="numeric"/>
                </div>
                {/* Speak + Add buttons — above all other inputs so keyboard NEVER covers them */}
                <div style={{display:"flex",gap:6,marginBottom:8}}>
                  <button onClick={voiceEntry} style={{flex:1,padding:12,background:listen&&lisF==="entry"?RD:GR,color:W,border:"none",borderRadius:10,cursor:"pointer",fontWeight:800,fontSize:15,fontFamily:"inherit"}}>
                    {listen&&lisF==="entry"?"🔴 Stop":"🎤 Speak"}
                  </button>
                  <button onClick={addE} style={{flex:1,padding:12,background:BL,color:W,border:"none",borderRadius:10,fontWeight:800,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>+ Add Entry</button>
                </div>
                {/* Live voice status */}
                {voiceStatus?<div style={{fontSize:13,color:listen&&lisF==="entry"?"#C0392B":GR,marginBottom:8,padding:"8px 12px",background:listen&&lisF==="entry"?"#FFF5F5":"#F0FAF4",borderRadius:10,border:"1px solid "+(listen&&lisF==="entry"?"#FBBCBC":"#A8D5B5"),lineHeight:1.4,wordBreak:"break-word"}}>{voiceStatus}</div>:null}
                {/* Time + Description — at bottom so keyboard can open safely */}
                <div style={{marginBottom:8}}>
                  <label style={lbl}>Time Spent</label>
                  <input style={{...inp,padding:"9px 8px"}} value={tSpent} onChange={e=>setTSpent(e.target.value)} placeholder="2h, 30m"/>
                </div>
                <label style={lbl}>What Was Done</label>
                <textarea style={{...inp,minHeight:56,resize:"vertical"}} value={desc} onChange={e=>setDesc(e.target.value)} placeholder="e.g. making good, rub down"/>
              </div>
              {entries.length>0 && (
                <div style={card}>
                  <div style={{fontWeight:800,fontSize:14,marginBottom:10}}>Entries ({entries.length})</div>
                  {entries.map(e=>(
                    <div key={e.id} style={{background:e.sec==="mg"?"#EBF3FD":"#F0FFF4",borderRadius:10,padding:"9px 12px",marginBottom:7}}>
                      {e.editing ? (
                        <div>
                          <div style={{display:"flex",gap:8,marginBottom:6}}>
                            <input style={{...inp,flex:"0 0 80px",fontSize:13,padding:"6px 8px"}} defaultValue={e.plot} onChange={ev=>setEntries(p=>p.map(x=>x.id===e.id?{...x,plot:ev.target.value}:x))} placeholder="Plot"/>
                            <input style={{...inp,flex:1,fontSize:13,padding:"6px 8px"}} defaultValue={e.tt} onChange={ev=>setEntries(p=>p.map(x=>x.id===e.id?{...x,tt:ev.target.value,mins:pMin(ev.target.value)}:x))} placeholder="Time"/>
                          </div>
                          <textarea style={{...inp,fontSize:13,padding:"6px 8px",minHeight:50,resize:"vertical",marginBottom:6}} defaultValue={e.desc} onChange={ev=>setEntries(p=>p.map(x=>x.id===e.id?{...x,desc:ev.target.value}:x))}/>
                          <button onClick={()=>setEntries(p=>p.map(x=>x.id===e.id?{...x,editing:false}:x))} style={{width:"100%",padding:"7px",background:GR,color:W,border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>✅ Done</button>
                        </div>
                      ) : (
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div style={{flex:1}} onClick={()=>setShowPreview(true)}>
                            <div style={{fontWeight:800,fontSize:13,color:e.sec==="mg"?BL:GR}}>PLT {e.plot}{e.tt?" · "+e.tt:""}</div>
                            <div style={{fontSize:13,marginTop:2}}>{e.desc}</div>
                          </div>
                          <div style={{display:"flex",gap:4,paddingLeft:8}}>
                            <button onClick={()=>setEntries(p=>p.map(x=>x.id===e.id?{...x,editing:true}:x))} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,padding:2}}>✏️</button>
                            <button onClick={()=>setEntries(p=>p.filter(x=>x.id!==e.id))} style={{background:"none",border:"none",cursor:"pointer",color:MT,fontSize:20,padding:2}}>×</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={card}>
                <label style={lbl}>Materials / Plant / Access Equipment</label>
                <textarea style={{...inp,minHeight:60,resize:"vertical"}} value={mats} onChange={e=>setMats(e.target.value)} placeholder="List any materials or equipment used"/>
                <button onClick={()=>voice("mat")} style={{marginTop:8,padding:"8px 14px",background:listen&&lisF==="mat"?RD:"#F2F2F7",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"inherit",color:listen&&lisF==="mat"?W:DK}}>{listen&&lisF==="mat"?"🔴 Listening...":"🎤 Speak"}</button>
              </div>
              {fromSummary && (
                <button onClick={backToSummary} style={{width:"100%",padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginTop:4}}>✅ Done — Back to Summary</button>
              )}
            </div>
          )}

          {sc==="summary" && (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <button onClick={()=>setSc("sheet")} style={{background:"#F2F2F7",border:"none",borderRadius:8,padding:"7px 12px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:DK}}>‹ Back</button>
                <div style={{fontWeight:800,fontSize:16}}>Review & Submit</div>
              </div>
              <div style={{...card,background:"#F0FAF4",border:"1px solid #A8D5B5",marginBottom:8,padding:14}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}><span style={{fontSize:18}}>✅</span><span style={{fontWeight:800,fontSize:16}}>Shift Ended</span></div>
                    <div style={{fontSize:13,color:MT}}>{today} · {tIn}–{tOut}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:22,fontWeight:900,color:GR}}>{fMin(mgM+siM)}</div>
                  </div>
                </div>
              </div>

              {/* ── DAYWORK SHEET PREVIEW ── */}
              <div style={{fontSize:11,fontWeight:700,color:MT,textTransform:"uppercase",letterSpacing:".8px",marginBottom:6}}>Your Daywork Sheet</div>
              <div style={{width:"100%",overflow:"hidden",marginBottom:4,position:"relative",height:Math.round(SHEET_H*scale)+"px"}}>
                <div style={{transformOrigin:"top left",transform:"scale("+scale+")",width:SHEET_W,position:"absolute",top:0,left:0}}>
                  <div style={{width:SHEET_W,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
                    {/* HEADER — thinner: logo 56px, title alongside */}
                    <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
                      <div style={{flexShrink:0,width:56,height:56}}><img src={LOGO} alt="BAC" style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/></div>
                      <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span></div>
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr>
                        <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
                        <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{name}</td>
                        <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
                        <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{today}</td>
                      </tr>
                      <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{LC[site]||site}</td></tr>
                      <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{ctr}</td></tr>
                    </tbody></table>
                    {/* WORK SHEET — bigger */}
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr><td colSpan={2} style={{...CB,fontSize:11,padding:"2px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
                      <tr>
                        <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
                        <td style={{...C,width:75,textAlign:"right",fontWeight:700,fontSize:10,padding:"1px 4px"}}>Hours: {mgH}</td>
                      </tr>
                      <tr><td colSpan={2} style={{...C,height:380,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{mgTxt}</td></tr>
                    </tbody></table>
                    {/* SITE INSTRUCTION — smaller */}
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr>
                        <td style={{...CB,fontSize:11,padding:"2px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
                        <td style={{...CB,width:75,textAlign:"right",fontSize:10,padding:"2px 4px"}}>Hours: {siH}</td>
                      </tr>
                      <tr><td colSpan={2} style={{...C,height:140,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{siTxt}</td></tr>
                    </tbody></table>
                    <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr><td style={{...CB,fontSize:10,padding:"1px 4px"}}>Materials/Plant/Access Equipment Used</td></tr>
                      <tr><td style={{...C,height:30,verticalAlign:"top",paddingTop:2,fontSize:11}}>{mats}</td></tr>
                    </tbody></table>
                    <table style={{width:"45%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                      <tr>
                        <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME IN</td>
                        <td style={{...CB,textAlign:"center",width:"50%",fontSize:10,padding:"1px 4px"}}>TIME OUT</td>
                      </tr>
                      <tr>
                        <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tIn}</td>
                        <td style={{...C,textAlign:"center",fontSize:14,fontWeight:700,padding:"2px 4px"}}>{tOut}</td>
                      </tr>
                    </tbody></table>
                    <table style={{width:"100%",borderCollapse:"collapse"}}><tbody>
                      <tr>
                        <td style={{...CB,width:"28%",fontSize:9,padding:"2px 4px"}}>BAC Drywall Supervisor Signature</td>
                        <td style={{...C,width:"20%",padding:"2px 4px"}}>&nbsp;</td>
                        <td style={{...CB,width:"10%",fontSize:9,padding:"2px 4px"}}>Date</td>
                        <td style={{...C,width:"42%",padding:"2px 4px"}}>&nbsp;</td>
                      </tr>
                    </tbody></table>
                  </div>
                </div>
              </div>

              {/* ── ACTION BUTTONS — SWAPPED: Generate PDF top-right, Submit bottom-left ── */}
              <div style={{display:"flex",gap:8,marginBottom:6}}>
                <button onClick={goEdit} style={{flex:1,padding:11,background:W,border:"2px solid "+BL,borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",color:BL}}>✏️ Edit ({entries.length})</button>
                {!pdfUrl ? (
                  <button onClick={buildPDF} style={{flex:1,padding:11,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>📄 Generate PDF</button>
                ) : (
                  <a href={pdfUrl} download={"BAC-Daywork-"+site.replace(/ /g,"-")+"-"+today.replace(/\//g,"-")+".html"} style={{flex:1,padding:11,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6,textDecoration:"none",boxSizing:"border-box"}}>⬇️ Download</a>
                )}
              </div>
              {pdfUrl&&<div style={{fontSize:11,color:MT,textAlign:"center",marginBottom:6}}>Open downloaded file → Print → Save as PDF <button onClick={()=>setPdfUrl(null)} style={{background:"none",border:"none",color:BL,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>↺ Redo</button></div>}
              
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                {!submitted
                  ? <button onClick={submitShift} style={{flex:1,padding:11,background:GR,color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>📤 Submit</button>
                  : <div style={{flex:1,background:"#F0FAF4",border:"1px solid #A8D5B5",borderRadius:12,padding:11,fontSize:14,fontWeight:700,color:GR,textAlign:"center"}}>✅ Submitted</div>
                }
                <button onClick={newDay} style={{flex:1,padding:11,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>New Day ✓</button>
              </div>
              {!isOnline&&!submitted&&<div style={{fontSize:11,color:"#F59E0B",textAlign:"center",marginBottom:6}}>📵 Offline — will sync when back online</div>}

              {/* ── GPS LOG — at the very bottom ── */}
              {(gpsIn||gpsOut) && (
                <div style={{...card,marginBottom:8,padding:12}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:6}}>📍 GPS Log</div>
                  {gpsIn && <div style={{fontSize:11,marginBottom:4}}><strong>Clock In:</strong> {gpsIn.lat}, {gpsIn.lng} ±{gpsIn.acc}m <a href={"https://maps.google.com/?q="+gpsIn.lat+","+gpsIn.lng} target="_blank" rel="noreferrer" style={{color:BL}}>View map</a></div>}
                  {gpsOut && <div style={{fontSize:11}}><strong>Clock Out:</strong> {gpsOut.lat}, {gpsOut.lng} ±{gpsOut.acc}m <a href={"https://maps.google.com/?q="+gpsOut.lat+","+gpsOut.lng} target="_blank" rel="noreferrer" style={{color:BL}}>View map</a></div>}
                </div>
              )}
            </div>
          )}

          {/* ── SHEET PREVIEW MODAL (tap entry to view) ── */}
          {showPreview && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:300,display:"flex",flexDirection:"column"}}>
              <div style={{background:W,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+BD}}>
                <div style={{fontWeight:800,fontSize:16}}>Daywork Sheet Preview</div>
                <button onClick={()=>setShowPreview(false)} style={{background:"#F2F2F7",border:"none",borderRadius:8,padding:"7px 14px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✕ Close</button>
              </div>
              <div style={{fontSize:12,color:MT,textAlign:"center",padding:"6px 0",background:"#F9FAFB",borderBottom:"1px solid "+BD}}>Pinch to zoom · Tap ✏️ on an entry to edit</div>
              <div style={{flex:1,overflowY:"auto",overflowX:"auto",padding:12,touchAction:"pan-x pan-y"}}>
                <div style={{width:"100%",overflowX:"auto"}}>
                  <div style={{width:680,minWidth:680}}>
                    <div style={{width:680,background:"#fff",border:"1.5px solid #333",padding:"4px 10px 6px",fontFamily:"Arial,sans-serif",boxSizing:"border-box"}}>
                      <div style={{display:"flex",alignItems:"center",marginBottom:3,gap:12}}>
                        <div style={{flexShrink:0,width:56,height:56}}><img src={LOGO} alt="BAC" style={{width:"100%",height:"100%",objectFit:"contain",display:"block"}}/></div>
                        <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:900,fontSize:16,letterSpacing:2,textDecoration:"underline",color:"#111"}}>DAYWORK SHEET</span></div>
                      </div>
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr>
                          <td rowSpan={3} style={{...CB,width:"18%",textAlign:"center",fontSize:9,padding:"1px 3px"}}>SUB-CONTRACTOR NAME</td>
                          <td rowSpan={3} style={{...C,width:"25%",fontSize:13,fontWeight:700,textAlign:"center",padding:"1px 6px"}}>{name}</td>
                          <td style={{...CB,width:"19%",fontSize:9,padding:"1px 4px"}}>DATE</td>
                          <td style={{...C,width:"38%",fontSize:11,padding:"1px 4px"}}>{today}</td>
                        </tr>
                        <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>SITE LOCATION</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{LC[site]||site}</td></tr>
                        <tr><td style={{...CB,fontSize:9,padding:"1px 4px"}}>CONTRACTOR</td><td style={{...C,fontSize:11,padding:"1px 4px"}}>{ctr}</td></tr>
                      </tbody></table>
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr><td colSpan={2} style={{...CB,fontSize:10,padding:"1px 4px"}}>Work Sheet - Please Include Plot Numbers</td></tr>
                        <tr>
                          <td style={{...C,fontSize:9,color:"#CC0000",fontWeight:700,fontStyle:"italic",padding:"1px 4px"}}>Please confirm how long you spent in each plot next to your plot number</td>
                          <td style={{...C,width:75,textAlign:"right",fontWeight:700,fontSize:10,padding:"1px 4px"}}>Hours: {mgH}</td>
                        </tr>
                        <tr><td colSpan={2} style={{...C,height:200,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{mgTxt}</td></tr>
                      </tbody></table>
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:2}}><tbody>
                        <tr>
                          <td style={{...CB,fontSize:10,padding:"1px 4px"}}>Site Instruction Works - Please Include Plot Numbers</td>
                          <td style={{...CB,width:75,textAlign:"right",fontSize:10,padding:"1px 4px"}}>Hours: {siH}</td>
                        </tr>
                        <tr><td colSpan={2} style={{...C,height:80,verticalAlign:"top",paddingTop:5,whiteSpace:"pre-wrap",fontSize:11,lineHeight:1.7}}>{siTxt}</td></tr>
                      </tbody></table>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{padding:"12px 16px 24px",borderTop:"1px solid "+BD,background:W,display:"flex",gap:8}}>
                <button onClick={()=>setShowPreview(false)} style={{flex:1,padding:14,background:"#F2F2F7",color:DK,border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>‹ Back to entries</button>
                <button onClick={()=>{setShowPreview(false);}} style={{flex:1,padding:14,background:BL,color:W,border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✅ Looks good</button>
              </div>
            </div>
          )}

          {/* ── SITE NOT LISTED MODAL ── */}
          {showSiteRequest && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",padding:"24px 20px 40px"}}>
                {siteRequestSent ? (
                  <div style={{textAlign:"center",padding:"20px 0"}}>
                    <div style={{fontSize:48,marginBottom:12}}>✅</div>
                    <div style={{fontWeight:800,fontSize:18,marginBottom:6}}>Request sent</div>
                    <div style={{fontSize:14,color:MT,marginBottom:24}}>Office will add your site shortly. You're clocked in.</div>
                    <button onClick={()=>setShowSiteRequest(false)} style={{width:"100%",padding:14,background:BL,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Got it</button>
                  </div>
                ) : (
                  <div>
                    <div style={{fontWeight:900,fontSize:18,marginBottom:4}}>⚠️ Site not listed</div>
                    <div style={{fontSize:13,color:MT,marginBottom:16}}>You're clocked in. Tell the office which site you're on:</div>
                    <textarea style={{...{width:"100%",padding:"11px 13px",border:"1.5px solid "+BD,borderRadius:10,fontSize:15,boxSizing:"border-box",background:W,fontFamily:"inherit",outline:"none",color:DK},minHeight:80,resize:"vertical"}} placeholder="e.g. Haslemere — new Bellway site on London Road" value={siteRequestNote} onChange={e=>setSiteRequestNote(e.target.value)}/>
                    <button onClick={()=>{
                      const r={id:Date.now(),workerName:name,type:"wrong_site",note:"Site not listed: "+(siteRequestNote||"No details given"),date:today,submitted:new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),status:"pending",gps:gpsIn||null};
                      try{const ex=JSON.parse(localStorage.getItem("bac_requests")||"[]");ex.push(r);localStorage.setItem("bac_requests",JSON.stringify(ex));}catch(e){}
                      // Push to Supabase
                      pushRequest({workerId,workerName:name,type:"wrong_site",note:"Site not listed: "+(siteRequestNote||"No details given"),gps:gpsIn||null});
                      setSiteRequestSent(true);
                    }} style={{width:"100%",marginTop:12,padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Send to Office</button>
                    <button onClick={()=>{setShowSiteRequest(false);setSc("home");}} style={{width:"100%",marginTop:8,padding:12,background:"#F2F2F7",border:"none",borderRadius:12,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:DK}}>Skip — I'll sort it later</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {showEdit && editShift && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"90vh",overflowY:"auto",paddingBottom:40}}>
                {editDone ? (
                  <div style={{padding:40,textAlign:"center"}}>
                    <div style={{fontSize:64,marginBottom:16}}>👍</div>
                    <div style={{fontWeight:800,fontSize:18,marginBottom:8}}>Edit request sent</div>
                    <div style={{fontSize:14,color:MT,marginBottom:24}}>Sent for manager approval</div>
                    <button onClick={()=>setShowEdit(false)} style={{width:"100%",padding:16,background:BL,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Got it</button>
                  </div>
                ) : (
                  <div style={{padding:"20px 20px 0"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                      <button onClick={()=>setShowEdit(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:BL,padding:0}}>‹</button>
                      <div style={{fontWeight:900,fontSize:20}}>Edit Shift</div>
                    </div>
                    <div style={card}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Job</span>
                        <span style={{fontWeight:700,fontSize:14,background:"#E8F5E9",color:"#2E7D32",padding:"4px 10px",borderRadius:20}}>{LC[editShift.site]||editShift.site}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Date</span>
                        <span style={{fontWeight:700,fontSize:14}}>{editShift.date}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock In</span>
                        <input type="time" value={editIn} onChange={e=>setEditIn(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock Out</span>
                        <input type="time" value={editOut} onChange={e=>setEditOut(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div>
                        <div style={{color:MT,fontSize:13,marginBottom:6}}>📝 Note to manager</div>
                        <textarea style={{...inp,minHeight:80,resize:"vertical"}} value={editNote} onChange={e=>setEditNote(e.target.value)} placeholder="Attach a note to your request"/>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:MT,textAlign:"center",marginBottom:16}}>All requests will be sent for manager approval</div>
                    <button onClick={submitEdit} style={{width:"100%",padding:16,background:GR,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Send for Approval</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── WEEKLY SUMMARY MODAL ── */}
          {weekSummary&&(
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"90vh",overflowY:"auto",paddingBottom:40}}>
                <div style={{padding:"20px 20px 0"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                    <div>
                      <div style={{fontWeight:900,fontSize:20}}>📋 Week Summary</div>
                      <div style={{fontSize:13,color:MT,marginTop:2}}>{weekSummary.weekLabel}</div>
                    </div>
                    <button onClick={()=>setWeekSummary(null)} style={{background:"#F2F2F7",border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                  </div>

                  {/* Total hours card */}
                  <div style={{background:LBL,borderRadius:14,padding:16,marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:13,color:MT,fontWeight:600}}>Total hours this week</div>
                      <div style={{fontSize:32,fontWeight:900,color:BL}}>{fMin(weekSummary.totalMins)}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:13,color:MT,fontWeight:600}}>Shifts</div>
                      <div style={{fontSize:32,fontWeight:900,color:BL}}>{weekSummary.shifts.length}</div>
                    </div>
                  </div>

                  {/* Day by day breakdown */}
                  {Object.values(weekSummary.days).sort((a,b)=>{
                    const [da,ma,ya]=a.date.split("/");const [db,mb,yb]=b.date.split("/");
                    return new Date(ya,ma-1,da)-new Date(yb,mb-1,db);
                  }).map(day=>(
                    <div key={day.date} style={{...card,marginBottom:10}}>
                      <div style={{fontWeight:800,fontSize:15,marginBottom:8,color:DK}}>{day.date}</div>
                      {day.shifts.map((s,i)=>(
                        <div key={s.id} style={{paddingBottom:i<day.shifts.length-1?10:0,marginBottom:i<day.shifts.length-1?10:0,borderBottom:i<day.shifts.length-1?"1px solid "+BD:"none"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                            <div>
                              <div style={{fontWeight:700,fontSize:13}}>{LC[s.site]||s.site}</div>
                              <div style={{fontSize:12,color:MT}}>{s.tIn} – {s.tOut}</div>
                              {s.mgH&&<div style={{fontSize:11,color:MT}}>WS: {s.mgH}hr{s.siH?" · SI: "+s.siH+"hr":""}</div>}
                            </div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontWeight:800,fontSize:15,color:BL}}>{fMin(s.totalMins)}</div>
                              {s.gpsIn&&<a href={"https://maps.google.com/?q="+s.gpsIn.lat+","+s.gpsIn.lng} target="_blank" rel="noreferrer" style={{fontSize:10,color:BL}}>📍 Map</a>}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid "+BD,display:"flex",justifyContent:"space-between"}}>
                        <span style={{fontSize:12,color:MT,fontWeight:600}}>Day total</span>
                        <span style={{fontSize:13,fontWeight:800,color:GR}}>{fMin(day.shifts.reduce((a,s)=>a+(s.totalMins||0),0))}</span>
                      </div>
                    </div>
                  ))}

                  {/* Sites worked this week */}
                  <div style={card}>
                    <div style={{fontWeight:800,fontSize:14,marginBottom:10}}>Sites this week</div>
                    {[...new Set(weekSummary.shifts.map(s=>s.site))].map(s=>(
                      <div key={s} style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                        <span style={{fontSize:13}}>{LC[s]||s}</span>
                        <span style={{fontSize:13,fontWeight:700,color:BL}}>{fMin(weekSummary.shifts.filter(x=>x.site===s).reduce((a,x)=>a+(x.totalMins||0),0))}</span>
                      </div>
                    ))}
                  </div>

                  <button onClick={()=>setWeekSummary(null)} style={{width:"100%",padding:14,background:"#1C1C1E",color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                </div>
              </div>
            </div>
          )}

          {showAdd && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"90vh",overflowY:"auto",paddingBottom:40}}>
                {addDone ? (
                  <div style={{padding:40,textAlign:"center"}}>
                    <div style={{fontSize:64,marginBottom:16}}>👍</div>
                    <div style={{fontWeight:800,fontSize:18,marginBottom:8}}>Shift added</div>
                    <div style={{fontSize:14,color:MT,marginBottom:24}}>Sent for manager approval</div>
                    <button onClick={()=>setShowAdd(false)} style={{width:"100%",padding:16,background:BL,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Got it</button>
                  </div>
                ) : (
                  <div style={{padding:"20px 20px 0"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                      <button onClick={()=>setShowAdd(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:BL,padding:0}}>‹</button>
                      <div style={{fontWeight:900,fontSize:20}}>Add Shift</div>
                    </div>
                    <div style={card}>
                      <div style={{paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <div style={{color:MT,fontSize:13,marginBottom:6}}>Job / Site</div>
                        <button onClick={()=>setShowAddSite(true)} style={{width:"100%",padding:"10px 14px",background:"#F2F2F7",border:"1.5px solid "+BD,borderRadius:10,fontSize:14,fontWeight:700,textAlign:"left",cursor:"pointer",fontFamily:"inherit",color:addSite?"#2E7D32":MT}}>
                          {addSite||"Select site..."}
                        </button>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Date</span>
                        <input type="date" value={addDate} onChange={e=>setAddDate(e.target.value)} style={{border:"none",fontSize:14,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock In</span>
                        <input type="time" value={addIn} onChange={e=>setAddIn(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                        <span style={{color:MT,fontSize:14}}>Clock Out</span>
                        <input type="time" value={addOut} onChange={e=>setAddOut(e.target.value)} style={{border:"none",fontSize:18,fontWeight:700,color:BL,background:"transparent",outline:"none"}}/>
                      </div>
                      <div>
                        <div style={{color:MT,fontSize:13,marginBottom:6}}>📝 Note</div>
                        <textarea style={{...inp,minHeight:70,resize:"vertical"}} value={addNote} onChange={e=>setAddNote(e.target.value)} placeholder="e.g. New site, forgot to clock in..."/>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:MT,textAlign:"center",marginBottom:16}}>Manual shifts will be sent for manager approval</div>
                    <button onClick={submitAdd} style={{width:"100%",padding:16,background:GR,color:W,border:"none",borderRadius:14,fontSize:17,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Send for Approval</button>
                  </div>
                )}
              </div>
              {showAddSite && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:400,display:"flex",alignItems:"flex-end"}}>
                  <div style={{background:W,borderRadius:"20px 20px 0 0",width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column"}}>
                    <div style={{padding:"16px 20px 12px",borderBottom:"1px solid "+BD}}>
                      <div style={{textAlign:"center",fontSize:18,fontWeight:700,marginBottom:12}}>Select Site</div>
                      <input style={{...inp,background:"#F2F2F7"}} placeholder="Search sites..." value={addSrch} onChange={e=>setAddSrch(e.target.value)}/>
                    </div>
                    <div style={{overflowY:"auto",flex:1}}>
                      {addFilt.map(r=>(
                        <button key={r} onClick={()=>{setAddSite(r);setShowAddSite(false);setAddSrch("");}} style={{width:"100%",padding:"14px 20px",background:W,border:"none",borderBottom:"1px solid "+BD,cursor:"pointer",display:"flex",alignItems:"center",gap:14,fontSize:15,color:DK,fontFamily:"inherit",textAlign:"left"}}>
                          <div style={{width:44,height:44,borderRadius:6,background:W,border:"2px solid "+DOTS[SITES.indexOf(r)%DOTS.length],flexShrink:0,overflow:"hidden",padding:2,boxSizing:"border-box"}}>
                            <img src={LOGO} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/>
                          </div>
                          <div style={{flex:1}}>{r}</div>
                          {PC[r] && <span style={{fontSize:12,color:MT}}>{PC[r]}</span>}
                        </button>
                      ))}
                    </div>
                    <div style={{padding:"12px 20px 24px",borderTop:"1px solid "+BD}}>
                      <button onClick={()=>{setShowAddSite(false);setAddSrch("");}} style={{width:"100%",padding:14,background:"#F2F2F7",border:"none",borderRadius:12,fontSize:17,color:DK,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SETTINGS MODAL ── */}
          {showSettings && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",paddingBottom:40}}>
                <div style={{padding:"20px 20px 0"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
                    <div style={{fontWeight:900,fontSize:20}}>Settings</div>
                    <button onClick={()=>setShowSettings(false)} style={{background:"#F2F2F7",border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                  </div>
                  <div style={card}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:12,marginBottom:12,borderBottom:"1px solid "+BD}}>
                      <span style={{color:MT,fontSize:14}}>Signed in as</span>
                      <span style={{fontWeight:700,fontSize:14}}>{name}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{color:MT,fontSize:14}}>Role</span>
                      <span style={{fontWeight:700,fontSize:14,background:workerRole==="admin"?"#FEF3C7":"#E0E7FF",color:workerRole==="admin"?"#92400E":"#3730A3",padding:"3px 10px",borderRadius:12,textTransform:"capitalize"}}>{workerRole||"worker"}</span>
                    </div>
                  </div>
                  <button onClick={()=>{setShowSettings(false);setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);setShowPinChange(true);}} style={{width:"100%",padding:14,background:W,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",color:DK}}>
                    <span>🔐 Change my PIN</span><span style={{color:MT,fontSize:18}}>›</span>
                  </button>
                  <button onClick={signOut} style={{width:"100%",padding:14,background:W,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",color:RD}}>
                    <span>🚪 Sign out</span><span style={{color:MT,fontSize:18}}>›</span>
                  </button>
                  <div style={{textAlign:"center",fontSize:11,color:MT,marginTop:14}}>{BUILD}</div>
                </div>
              </div>
            </div>
          )}

          {/* ── PIN PROMPT MODAL (after first login on temp PIN) ── */}
          {showPinPrompt && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:400,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",padding:"24px 20px 40px"}}>
                <div style={{textAlign:"center",fontSize:40,marginBottom:8}}>🔐</div>
                <div style={{fontWeight:900,fontSize:20,textAlign:"center",marginBottom:8}}>Welcome, {name.split(" ")[0]}</div>
                <div style={{fontSize:14,color:MT,textAlign:"center",marginBottom:6,lineHeight:1.5}}>Your PIN is private to you. Would you like to change it now to one only you know?</div>
                <div style={{fontSize:12,color:MT,textAlign:"center",marginBottom:20,fontStyle:"italic"}}>You can change it any time from Settings.</div>
                <button onClick={()=>{setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);setShowPinChange(true);}} style={{width:"100%",padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginBottom:8}}>Yes, set my own PIN</button>
                <button onClick={keepTempPin} style={{width:"100%",padding:14,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Keep this PIN for now</button>
              </div>
            </div>
          )}

          {/* ── PIN CHANGE MODAL ── */}
          {showPinChange && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:500,display:"flex",alignItems:"flex-end"}}>
              <div style={{background:BG,borderRadius:"20px 20px 0 0",width:"100%",padding:"20px 20px 40px",maxHeight:"95vh",overflowY:"auto"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontWeight:900,fontSize:20}}>Change PIN</div>
                  <button onClick={()=>{setShowPinChange(false);setNewPin1("");setNewPin2("");setPinChangeError("");setPinStep(1);}} style={{background:"#F2F2F7",border:"none",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </div>
                <div style={{fontSize:13,color:MT,marginBottom:12,textAlign:"center"}}>
                  {pinStep===1?"Enter a new 4-digit PIN":"Confirm your new PIN"}
                </div>
                {pinDots(pinStep===1?newPin1:newPin2,4)}
                {pinChangeError && <div style={{textAlign:"center",color:RD,fontSize:13,fontWeight:600,marginBottom:12}}>{pinChangeError}</div>}
                <PinPad
                  value={pinStep===1?newPin1:newPin2}
                  onPress={d=>{
                    if(pinStep===1){
                      if(newPin1.length>=4)return;
                      const next=newPin1+d;
                      setNewPin1(next);
                      // Auto-advance to confirm step at 4 digits
                      if(next.length===4)setTimeout(()=>setPinStep(2),200);
                    } else {
                      if(newPin2.length>=4)return;
                      setNewPin2(p=>p+d);
                    }
                    setPinChangeError("");
                  }}
                  onClear={()=>{
                    if(pinStep===1)setNewPin1("");
                    else setNewPin2("");
                    setPinChangeError("");
                  }}
                  onBack={()=>{
                    if(pinStep===1)setNewPin1(p=>p.slice(0,-1));
                    else setNewPin2(p=>p.slice(0,-1));
                    setPinChangeError("");
                  }}
                />
                {pinStep===2 && (
                  <div style={{display:"flex",gap:8,marginTop:16}}>
                    <button onClick={()=>{setNewPin2("");setPinStep(1);}} style={{flex:1,padding:14,background:W,color:DK,border:"1.5px solid "+BD,borderRadius:12,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>‹ Back</button>
                    {newPin2.length>=4 && (
                      <button onClick={savePinChange} style={{flex:2,padding:14,background:GR,color:W,border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>✅ Save</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
